import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, getDocs, onSnapshot, query, orderBy, doc, addDoc, updateDoc, serverTimestamp, getDoc, where, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import Table, { Column } from '../../../../components/Table';
import { Markdown, MarkdownEditor } from '../../../../components/Markdown';
import { marked } from 'marked';
import { FiPlus, FiMinus, FiEye, FiSearch, FiX, FiCheck, FiCornerUpRight, FiChevronDown, FiMail, FiTrash2, FiRotateCcw } from 'react-icons/fi';
import { fetchManagedPsoIds } from '../../../protocol/service/protocolService';

type PsoRequestItem = {
    itemId?: string;
    itemCode?: string;
    itemName: string;
    specification: string;
    quantity: number;
    location: string;
    coverageDetail: string;
    requirementType?: 'Complaince' | 'Additional';
    amount?: string | number;
    totalAmount?: string | number;
    unit?: string;
};

type PsoRequest = {
    id: string;
    requestId: string;
    docType: 'pso_request';
    requestType: 'BAMS';
    requestCategory: 'Replacement' | 'Additional' | 'New';
    dateOfRequest: string;
    branchCode: string;
    branchName: string;
    branchId: string;
    vendor?: string;
    vendorId?: string;
    coveredArea?: string;
    floorsConsistOn?: string;
    items: PsoRequestItem[];
    remarks: string;
    status: 'pending' | 'forwarded_to_regional' | 'forwarded' | 'approved' | 'rejected';
    inchargeRemarks?: string;
    isPOGenerated: boolean;
    createdBy: string;
    psoId: string;
    psoName: string;
    createdAt?: any;
    updatedAt?: any;
    rejectReason?: string;
    rejectedBy?: string;
    rejectedByRole?: string;
    approverRemarks?: string;
    amount?: string | number;
    resubmitted?: boolean;
    resubmittedAt?: any;
    resubmissionReason?: string;
};

interface BAMSPsoRequestProps {
    companyId: string | null;
    branches: any[];
    vendors: any[];
    deployments: any[];
    uid: string | undefined;
    pso: any;
    onInitiatePO: (request: PsoRequest) => void;
    openRequest?: any | null;
    onCloseViewRequest?: () => void;
    hideList?: boolean;
    summaryMode?: boolean;
}

interface SearchableItemSelectProps {
    items: any[];
    value: string;
    onChange: (item: any) => void;
    placeholder?: string;
}

function SearchableItemSelect({
    items,
    value,
    onChange,
    placeholder = "Select Item..."
}: SearchableItemSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedItem = items.find(item => item.id === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = items.filter(item => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            item.itemCode?.toLowerCase().includes(s) ||
            item.itemName?.toLowerCase().includes(s) ||
            item.itemDescription?.toLowerCase().includes(s) ||
            item.brandName?.toLowerCase().includes(s)
        );
    });

    return (
        <div className="relative w-full" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex h-10 items-center justify-between rounded-lg border border-gray-300 bg-white py-2 px-3 text-left text-sm text-gray-950 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
                <span className="truncate">
                    {selectedItem 
                        ? `${selectedItem.itemCode} - ${selectedItem.itemName} (Rs. ${Number(selectedItem.rates || 0).toLocaleString()})`
                        : placeholder}
                </span>
                <Icon
                    icon={FiChevronDown}
                    className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute z-[100] mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-800">
                    <div className="border-b border-gray-200 p-2 dark:border-gray-700">
                        <div className="relative">
                            <Icon
                                icon={FiSearch}
                                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, code, brand..."
                                className="w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-9 pr-3 text-sm font-normal text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                No items found
                            </div>
                        ) : (
                            filtered.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => {
                                        onChange(item);
                                        setIsOpen(false);
                                        setSearch('');
                                    }}
                                    className={`w-full px-4 py-2.5 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                                        value === item.id ? 'bg-blue-50 dark:bg-gray-700' : ''
                                    }`}
                                >
                                    <div className="font-medium text-gray-950 dark:text-gray-100">
                                        {item.itemCode} - {item.itemName}
                                    </div>
                                    {item.itemDescription && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                            {item.itemDescription}
                                        </div>
                                    )}
                                    <div className="text-xs font-semibold text-green-600 dark:text-green-400 mt-0.5">
                                        Rs. {Number(item.rates || 0).toLocaleString()}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function BAMSPsoRequest({
    companyId,
    branches,
    vendors,
    deployments,
    uid,
    pso,
    onInitiatePO,
    openRequest,
    onCloseViewRequest,
    hideList = false,
    summaryMode = false
}: BAMSPsoRequestProps) {
    const psoDocId = sessionStorage.getItem('psoDocId') || '';
    const userRole = sessionStorage.getItem('role') || '';
    const designation = pso?.designation || '';

    const isRegional = /regional/i.test(designation);
    const isHead = /(^|\s)head(\s|$)/i.test(designation.replace(/_/g, ' ')) && !isRegional;
    const isPSO = userRole === 'pso' && !isRegional && !isHead;
    const isBank = userRole === 'bank';
    const isIncharge = /incharge/i.test(designation);

    const [requests, setRequests] = useState<PsoRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<PsoRequest | null>(null);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [branchSearch, setBranchSearch] = useState('');

    // Reject Modal
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [forwardRemarks, setForwardRemarks] = useState('');
    const [originalRequestItems, setOriginalRequestItems] = useState<any[] | null>(null);
    const [forwardStep, setForwardStep] = useState<'none' | 'ask_change' | 'show_remarks'>('none');
    const [editForwardStep, setEditForwardStep] = useState<'none' | 'show_remarks'>('none');

    // Directly open modal when openRequest is provided (from RequestSummary)
    useEffect(() => {
        if (openRequest) {
            setSelectedRequest(openRequest as PsoRequest);
            setShowDetailsModal(true);
        }
    }, [openRequest]);

    useEffect(() => {
        if (!showDetailsModal) {
            setOriginalRequestItems(null);
            setForwardStep('none');
            setEditForwardStep('none');
            setForwardRemarks('');
            onCloseViewRequest?.();
        }
    }, [showDetailsModal]);

    const computeAutoRemarks = (originalItems: any[] | null, currentItems: any[]) => {
        if (!originalItems) return '';
        const changes: string[] = [];
        const normalize = (str: any) => String(str || '').trim().toLowerCase();

        // Find removed items
        originalItems.forEach(orig => {
            const found = currentItems.find(item => 
                normalize(item.itemName) === normalize(orig.itemName) && 
                normalize(item.specification) === normalize(orig.specification)
            );
            if (!found) {
                const specSuffix = orig.specification ? ` (${orig.specification})` : '';
                changes.push(`- Removed: ${orig.itemName}${specSuffix}`);
            }
        });

        // Find added or changed items
        currentItems.forEach(newItem => {
            const orig = originalItems.find(item => 
                normalize(item.itemName) === normalize(newItem.itemName) && 
                normalize(item.specification) === normalize(newItem.specification)
            );
            if (!orig) {
                const specSuffix = newItem.specification ? ` (${newItem.specification})` : '';
                changes.push(`- Added: ${newItem.itemName}${specSuffix} (Qty: ${newItem.quantity})`);
            } else {
                if (Number(orig.quantity) !== Number(newItem.quantity)) {
                    changes.push(`- Changed Qty of ${newItem.itemName}: from ${orig.quantity} to ${newItem.quantity}`);
                }
                if (Number(orig.amount) !== Number(newItem.amount)) {
                    changes.push(`- Changed Est. Price of ${newItem.itemName}: from Rs. ${orig.amount} to Rs. ${newItem.amount}`);
                }
            }
        });

        if (changes.length === 0) return '';
        return `**Item changes made by Incharge:**\n${changes.join('\n')}\n\n`;
    };

    // Toast
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [showToast, setShowToast] = useState(false);
    const [isActionLoading, setIsActionLoading] = useState(false);

    // PO mapping states
    const [poRequests, setPoRequests] = useState<any[]>([]);
    const [inventoryItems, setInventoryItems] = useState<any[]>([]);
    const [taxPercentage, setTaxPercentage] = useState<number>(18);
    const [selectedPo, setSelectedPo] = useState<any | null>(null);
    const [showPoModal, setShowPoModal] = useState(false);
    const [poPsoStatus, setPoPsoStatus] = useState<'installed' | 'not installed'>('not installed');
    const [poInstallationDate, setPoInstallationDate] = useState<string>('');

    // Email Modal states
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailIntro, setEmailIntro] = useState('');
    const [emailOutro, setEmailOutro] = useState('');
    const [emailSignature, setEmailSignature] = useState('');
    const [emailActiveTab, setEmailActiveTab] = useState<'edit' | 'preview'>('edit');

    const [bankDetails, setBankDetails] = useState<{ name: string; imgUrl: string } | null>(null);

    const [mobilePage, setMobilePage] = useState(1);
    const mobileItemsPerPage = 5;

    useEffect(() => {
        setMobilePage(1);
    }, [searchTerm, statusFilter]);

    const initEmailComposer = async (request: PsoRequest) => {
        setSelectedRequest(request);
        setEmailRecipients([]);
        setEmailInput('');

        const initialRecipients: string[] = [];

        // Fetch vendor details
        const vendorObj = vendors.find(v => v.companyName === request.vendor || v.id === request.vendorId || v.companyId === request.vendorId);
        if (vendorObj) {
            if (vendorObj.email) {
                initialRecipients.push(vendorObj.email);
            }
            if (vendorObj.contactEmail && vendorObj.contactEmail !== vendorObj.email) {
                initialRecipients.push(vendorObj.contactEmail);
            }
        }

        // Add creator's email
        if (request.createdBy && companyId) {
            try {
                const creatorDoc = await getDoc(doc(db, 'banks', companyId, 'pos', request.createdBy));
                if (creatorDoc.exists()) {
                    const creatorData = creatorDoc.data();
                    if (creatorData.email) {
                        initialRecipients.push(creatorData.email);
                    }
                }
            } catch (err) {
                console.error('Error fetching creator email:', err);
            }
        }

        // Add current user's email so they get the copy instantly (matching PO Request)
        const currentUserEmail = auth.currentUser?.email;
        if (currentUserEmail) {
            initialRecipients.push(currentUserEmail);
        }

        const uniqueRecipients = Array.from(new Set(initialRecipients));
        setEmailRecipients(uniqueRecipients);

        // Fetch bank details
        let bankName = '';
        let bankLogoUrl = '';
        if (companyId) {
            try {
                const bankDoc = await getDoc(doc(db, 'banks', companyId));
                if (bankDoc.exists()) {
                    const bankData = bankDoc.data();
                    bankName = bankData.name || '';
                    bankLogoUrl = bankData.imgUrl || '';
                }
            } catch (err) {
                console.error('Error fetching bank details:', err);
            }
        }

        // Populate email templates
        const vendorContactPerson = vendorObj?.contactPersonName || 'Vendor Team';
        const vendorName = vendorObj?.companyName || request.vendor || 'Vendor';
        const branchName = request.branchName || '';
        const type = request.requestType || 'BAMS';

        const subject = `${bankName} ${type} Hardware Request - ${request.requestId} - ${branchName}`;
        setEmailSubject(subject);

        const introText = `Dear Mr./Ms ${vendorContactPerson} M/S ${vendorName},\n\nAs per approval of head Security Services and existing SLA with ${vendorName} ; please procure and install the below mentioned ${type} Hardware at our newly opened ${bankName} ${branchName} Branch ${branchName} Region (ABEP-26).`;
        setEmailIntro(introText);

        const outroText = `Vendor Team:\n\nPlease ensure the following points need to be accomplished after the installation of said hardware.\n\n1. Complete and proper Delivery Challan (Make / Quantity / Serial No.) should be properly filled and signed from relevant vendor and branch staff.\n2. Please confirm when installation has done.\n\nAbove all; please note that payment will be made as per the above mentioned hardware only; if any additional hardware would be required then PSO will take separate approval of these items from concerned management prior to invoicing.`;
        setEmailOutro(outroText);

        const userName = pso?.name || '';
        const userDesignation = pso?.designation || '';
        const signatureText = `Regards,\n\n${userName}\n${userDesignation}\nSecurity Services\n5th Floor | Zone B | HBL Plaza | I.I Chundrigar Road,\nKarachi, Pakistan\nPABX: +92 21 3241 8000 Ext.: 19268`;
        setEmailSignature(signatureText);

        setEmailActiveTab('edit');
        setBankDetails({ name: bankName, imgUrl: bankLogoUrl });
        setShowEmailModal(true);
    };

    // Form data for new PSO request
    const [formData, setFormData] = useState({
        dateOfRequest: new Date().toISOString().split('T')[0],
        branchCode: '',
        branchName: '',
        branchId: '',
        vendor: '',
        vendorId: '',
        coveredArea: '',
        floorsConsistOn: '',
        remarks: '',
        requestCategory: 'New' as 'Replacement' | 'Additional' | 'New',
        items: [] as PsoRequestItem[],
        amount: '',
        requestType: 'BAMS' as 'BAMS'
    });

    const [showVendorModal, setShowVendorModal] = useState(false);
    const [vendorSearch, setVendorSearch] = useState('');

    // Item modal inside Add Modal
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [newItem, setNewItem] = useState<PsoRequestItem>({
        itemName: '',
        specification: '',
        quantity: 1,
        location: '',
        coverageDetail: '',
        requirementType: 'Complaince',
        amount: '',
        totalAmount: '',
        unit: 'piece'
    });
    const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<string>('');

    // Reset add item modal states on open/close
    useEffect(() => {
        if (!showAddItemModal) {
            setSelectedInventoryItemId('');
            setNewItem({
                itemName: '',
                specification: '',
                quantity: 1,
                location: '',
                coverageDetail: '',
                requirementType: 'Complaince',
                amount: '',
                totalAmount: '',
                unit: 'piece'
            });
        }
    }, [showAddItemModal]);

    // Edit details states
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [editFormData, setEditFormData] = useState({
        dateOfRequest: '',
        branchCode: '',
        branchName: '',
        branchId: '',
        vendor: '',
        vendorId: '',
        coveredArea: '',
        floorsConsistOn: '',
        remarks: '',
        requestCategory: 'New' as 'Replacement' | 'Additional' | 'New',
        items: [] as PsoRequestItem[],
        amount: '',
        requestType: 'BAMS' as 'BAMS'
    });
    const [showEditBranchModal, setShowEditBranchModal] = useState(false);
    const [showEditVendorModal, setShowEditVendorModal] = useState(false);
    const [showEditAddItemModal, setShowEditAddItemModal] = useState(false);
    const [editBranchSearch, setEditBranchSearch] = useState('');
    const [editVendorSearch, setEditVendorSearch] = useState('');
    const [editNewItem, setEditNewItem] = useState<PsoRequestItem>({
        itemName: '',
        specification: '',
        quantity: 1,
        location: '',
        coverageDetail: '',
        requirementType: 'Complaince',
        amount: '',
        totalAmount: '',
        unit: 'piece'
    });
    const [selectedEditInventoryItemId, setSelectedEditInventoryItemId] = useState<string>('');

    const [showResubmitModal, setShowResubmitModal] = useState(false);
    const [resubmitReasonDraft, setResubmitReasonDraft] = useState('');
    const [resubmitRequestObj, setResubmitRequestObj] = useState<PsoRequest | null>(null);
    const [resubmitType, setResubmitType] = useState<'BAMS' | 'CCTV'>('BAMS');

    // Dropdown backdrop helper â€“ renders an invisible full-screen overlay so that
    // clicking anywhere outside the dropdown list reliably closes it, even inside portals.
    const DropdownBackdrop = ({ onClose }: { onClose: () => void }) => (
        <div
            className="fixed inset-0 z-40"
            onMouseDown={(e) => { e.stopPropagation(); onClose(); }}
        />
    );

    // Reset edit add item modal states on open/close
    useEffect(() => {
        if (!showEditAddItemModal) {
            setSelectedEditInventoryItemId('');
            setEditNewItem({
                itemName: '',
                specification: '',
                quantity: 1,
                location: '',
                coverageDetail: '',
                requirementType: 'Complaince',
                amount: '',
                totalAmount: '',
                unit: 'piece'
            });
        }
    }, [showEditAddItemModal]);



    const triggerToast = (msg: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToastMessage(msg);
        setToastType(type);
        setShowToast(true);
    };

    // Load managed PSO IDs for Regional role
    useEffect(() => {
        if (isRegional && companyId && psoDocId) {
            fetchManagedPsoIds(companyId, psoDocId)
                .then(setManagedPsoIds)
                .catch(err => console.error("Error fetching managed PSOs:", err));
        }
    }, [isRegional, companyId, psoDocId]);

    // Fetch requests - real-time stream
    useEffect(() => {
        if (!companyId) return;
        setLoading(true);
        const q = query(
            collection(db, 'banks', companyId, 'hardware_requests'),
            orderBy('createdAt', 'desc')
        );
        const unsub = onSnapshot(q, async (snap) => {
            const allItems = await Promise.all(
                snap.docs.map(async (docSnap) => {
                    const data = { id: docSnap.id, ...docSnap.data() } as any;
                    if (!data.items || data.items.length === 0) {
                        try {
                            const itemsSnap = await getDocs(
                                collection(db, 'banks', companyId, 'hardware_requests', docSnap.id, 'items')
                            );
                            data.items = itemsSnap.docs.map(i => ({ id: i.id, ...i.data() }));
                        } catch { data.items = []; }
                    }
                    return data;
                })
            );
            let psoRequests = allItems.filter((r: any) =>
                r.requestType === 'BAMS' &&
                r.docType === 'pso_request' &&
                (r.requestId || '').startsWith('BAMS')
            ) as PsoRequest[];
            // In summaryMode: only show requests that need attention
            if (summaryMode) {
                if (isRegional) {
                    psoRequests = psoRequests.filter(r => r.status === 'forwarded_to_regional');
                } else if (isHead) {
                    psoRequests = psoRequests.filter(r => r.status === 'forwarded');
                } else {
                    psoRequests = psoRequests.filter(r =>
                        r.status === 'forwarded_to_regional' || r.status === 'forwarded'
                    );
                }
            }
            const poReqs = allItems.filter((r: any) => r.requestType === 'BAMS' && r.docType !== 'pso_request');
            setRequests(psoRequests);
            setPoRequests(poReqs);
            setLoading(false);
        }, (err) => {
            console.error('Error streaming BAMS requests:', err);
            setLoading(false);
        });
        return () => unsub();
    }, [companyId, summaryMode, isRegional, isHead]);

    // Fetch inventory items
    useEffect(() => {
        if (!companyId) return;
        const fetchInventory = async () => {
            try {
                const itemsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'inventory'),
                        orderBy('createdAt', 'desc')
                    )
                );
                const itemsList = itemsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setInventoryItems(itemsList);
            } catch (error) {
                console.error('Error fetching inventory items:', error);
            }
        };
        fetchInventory();
    }, [companyId]);

    const filteredInventoryItems = useMemo(() => {
        return inventoryItems.filter(item => {
            const cat = item.category?.toUpperCase();
            return cat === 'BAMS';
        });
    }, [inventoryItems]);

    // Fetch tax percentage from localStorage
    useEffect(() => {
        const storedBAMSTax = localStorage.getItem('BAMS_tax_percentage');
        if (storedBAMSTax) {
            setTaxPercentage(parseFloat(storedBAMSTax));
        }
    }, []);

    // Generate BAMS Request ID like BAMS-RQ-12345
    const generateRequestId = () => {
        const rand = Math.floor(10000 + Math.random() * 90000);
        return `BAMS-RQ-${rand}`;
    };

    // Helper function to get deployed vendor from deployments collection
    const getDeployedVendorForBranch = (branchId: string) => {
        const branchDeployments = deployments.filter(d => d.branchId === branchId);

        for (const deployment of branchDeployments) {
            const deployedVendor = vendors.find(v =>
                v.id === deployment.companiesId ||
                v.companyId === deployment.companiesId
            );
            if (deployedVendor) {
                return {
                    vendorId: deployedVendor.companyId || deployedVendor.id,
                    vendor: deployedVendor.companyName || ''
                };
            }
        }
        return null;
    };

    const handleBranchSelect = (branch: any) => {
        const updatedFormData: any = {
            branchId: branch.id,
            branchCode: branch.branchCode || '',
            branchName: branch.address || branch.name || '',
            floorsConsistOn: branch.coveredArea || branch.floorsConsistOn || branch.floorConsistOn || '',
            coveredArea: branch.coveredAreaSqFt
                ? Number(branch.coveredAreaSqFt).toLocaleString()
                : (branch.coveredArea && !isNaN(Number(branch.coveredArea))
                    ? Number(branch.coveredArea).toLocaleString()
                    : ''),
        };

        const vendorInfo = getDeployedVendorForBranch(branch.id);
        if (vendorInfo) {
            updatedFormData.vendorId = vendorInfo.vendorId;
            updatedFormData.vendor = vendorInfo.vendor;
        } else {
            // Fallback to legacy branch.BAMSVendorId if exists
            const legacyVendor = vendors.find(v => v.id === branch.BAMSVendorId || v.companyId === branch.BAMSVendorId);
            if (legacyVendor) {
                updatedFormData.vendorId = legacyVendor.companyId || legacyVendor.id;
                updatedFormData.vendor = legacyVendor.companyName || '';
            } else {
                updatedFormData.vendorId = '';
                updatedFormData.vendor = '';
            }
        }

        setFormData(prev => ({
            ...prev,
            ...updatedFormData
        }));
        setShowBranchModal(false);
        setBranchSearch('');
    };

    const handleVendorSelect = (vendor: any) => {
        setFormData(prev => ({
            ...prev,
            vendorId: vendor.companyId || vendor.id,
            vendor: vendor.companyName || ''
        }));
        setShowVendorModal(false);
        setVendorSearch('');
    };

    const handleAddItem = () => {
        if (!newItem.itemName.trim()) {
            triggerToast('Item Name is required', 'error');
            return;
        }
        const qty = Number(newItem.quantity) || 1;
        const amt = Number(newItem.amount) || 0;
        const itemToAdd = {
            ...newItem,
            totalAmount: qty * amt,
            unit: newItem.unit || 'piece'
        };
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, itemToAdd]
        }));
        setNewItem({
            itemName: '',
            specification: '',
            quantity: 1,
            location: '',
            coverageDetail: '',
            requirementType: 'Complaince',
            amount: '',
            totalAmount: '',
            unit: 'piece'
        });
        setShowAddItemModal(false);
    };

    const handleRemoveItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Edit request handlers
    const handleStartEditing = () => {
        if (!selectedRequest) return;
        if (!originalRequestItems) {
            setOriginalRequestItems([...selectedRequest.items]);
        }
        setEditFormData({
            dateOfRequest: selectedRequest.dateOfRequest || '',
            branchCode: selectedRequest.branchCode || '',
            branchName: selectedRequest.branchName || '',
            branchId: selectedRequest.branchId || '',
            vendor: selectedRequest.vendor || '',
            vendorId: selectedRequest.vendorId || '',
            coveredArea: selectedRequest.coveredArea || '',
            floorsConsistOn: selectedRequest.floorsConsistOn || '',
            remarks: selectedRequest.remarks || '',
            requestCategory: (selectedRequest.requestCategory || 'New') as 'Replacement' | 'Additional' | 'New',
            items: [...selectedRequest.items],
            amount: selectedRequest.amount !== undefined ? String(selectedRequest.amount) : '',
            requestType: (String(selectedRequest.requestType) || 'BAMS') as 'BAMS'
        });
        setIsEditingDetails(true);
    };

    const handleEditBranchSelect = (branch: any) => {
        let deployedVendorId = '';
        let deployedVendorName = '';

        const branchDeployments = deployments.filter(d => d.branchId === branch.id);
        for (const deployment of branchDeployments) {
            const deployedVendor = vendors.find(v => v.id === deployment.companiesId || v.companyId === deployment.companiesId);
            if (deployedVendor) {
                deployedVendorId = deployedVendor.companyId || deployedVendor.id;
                deployedVendorName = deployedVendor.companyName || '';
                break;
            }
        }

        if (!deployedVendorId) {
            const fallbackVendorId = branch.BAMSVendorId || branch.vendorId || '';
            const fallbackVendor = vendors.find(v => v.id === fallbackVendorId || v.companyId === fallbackVendorId);
            if (fallbackVendor) {
                deployedVendorId = fallbackVendor.companyId || fallbackVendor.id;
                deployedVendorName = fallbackVendor.companyName || '';
            }
        }

        setEditFormData(prev => ({
            ...prev,
            branchId: branch.id,
            branchCode: branch.branchCode || '',
            branchName: branch.branchName || branch.name || '',
            floorsConsistOn: branch.floorsConsistOn || '',
            vendorId: deployedVendorId || 'ESSPL',
            vendor: deployedVendorName || 'ESSPL'
        }));
        setShowEditBranchModal(false);
        setEditBranchSearch('');
    };

    const handleEditVendorSelect = (vendor: any) => {
        setEditFormData(prev => ({
            ...prev,
            vendorId: vendor.companyId || vendor.id,
            vendor: vendor.companyName || ''
        }));
        setShowEditVendorModal(false);
        setEditVendorSearch('');
    };

    const handleEditAddItem = () => {
        if (!editNewItem.itemName.trim()) {
            triggerToast('Item Name is required', 'error');
            return;
        }
        const qty = Number(editNewItem.quantity) || 1;
        const amt = Number(editNewItem.amount) || 0;
        const itemToAdd = {
            ...editNewItem,
            totalAmount: qty * amt,
            unit: editNewItem.unit || 'piece'
        };
        setEditFormData(prev => ({
            ...prev,
            items: [...prev.items, itemToAdd]
        }));
        setEditNewItem({
            itemName: '',
            specification: '',
            quantity: 1,
            location: '',
            coverageDetail: '',
            requirementType: 'Complaince',
            amount: '',
            totalAmount: '',
            unit: 'piece'
        });
        setShowEditAddItemModal(false);
    };

    const handleEditRemoveItem = (index: number) => {
        setEditFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleSaveEdit = async () => {
        if (!companyId || !selectedRequest || isActionLoading) return;
        if (!editFormData.branchId) {
            triggerToast('Please select a branch', 'error');
            return;
        }
        if (editFormData.items.length === 0) {
            triggerToast('Please add at least one item', 'error');
            return;
        }

        setIsActionLoading(true);
        try {
            const updatedDoc = {
                requestCategory: editFormData.requestCategory,
                dateOfRequest: editFormData.dateOfRequest,
                branchCode: editFormData.branchCode,
                branchName: editFormData.branchName,
                branchId: editFormData.branchId,
                vendor: editFormData.vendor || '',
                vendorId: editFormData.vendorId || '',
                coveredArea: editFormData.coveredArea || '',
                floorsConsistOn: editFormData.floorsConsistOn || '',
                items: editFormData.items,
                remarks: editFormData.remarks,
                amount: editFormData.items.reduce((sum, item) => sum + (Number(item.totalAmount || (Number(item.amount) * Number(item.quantity))) || 0), 0),
                requestType: 'BAMS',
                updatedAt: serverTimestamp()
            };

            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), updatedDoc);
            triggerToast('PSO Request updated successfully', 'success');
            setIsEditingDetails(false);
            setSelectedRequest(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    ...updatedDoc
                } as any;
            });
        } catch (error) {
            console.error("Error updating PSO request:", error);
            triggerToast('Failed to update PSO request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleSaveAndForward = async () => {
        if (!companyId || !selectedRequest || isActionLoading) return;
        if (!editFormData.branchId) {
            triggerToast('Please select a branch', 'error');
            return;
        }
        if (editFormData.items.length === 0) {
            triggerToast('Please add at least one item', 'error');
            return;
        }

        setIsActionLoading(true);
        try {
            const updatedDoc = {
                requestCategory: editFormData.requestCategory,
                dateOfRequest: editFormData.dateOfRequest,
                branchCode: editFormData.branchCode,
                branchName: editFormData.branchName,
                branchId: editFormData.branchId,
                vendor: editFormData.vendor || '',
                vendorId: editFormData.vendorId || '',
                coveredArea: editFormData.coveredArea || '',
                floorsConsistOn: editFormData.floorsConsistOn || '',
                items: editFormData.items,
                remarks: editFormData.remarks,
                amount: editFormData.items.reduce((sum, item) => sum + (Number(item.totalAmount || (Number(item.amount) * Number(item.quantity))) || 0), 0),
                requestType: 'BAMS',
                status: 'forwarded_to_regional',
                inchargeRemarks: forwardRemarks || '',
                updatedAt: serverTimestamp()
            };

            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), updatedDoc);
            triggerToast('PSO Request updated and forwarded successfully', 'success');
            setIsEditingDetails(false);
            setEditForwardStep('none');
            setForwardRemarks('');
            setShowDetailsModal(false);
        } catch (error) {
            console.error("Error updating and forwarding PSO request:", error);
            triggerToast('Failed to update and forward PSO request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleSaveRequest = async () => {
        if (!companyId || !uid) return;
        if (!formData.branchId) {
            triggerToast('Please select a branch', 'error');
            return;
        }
        if (formData.items.length === 0) {
            triggerToast('Please add at least one item', 'error');
            return;
        }

        setIsActionLoading(true);
        try {
            const reqId = generateRequestId();
            const newRequest = {
                requestId: reqId,
                docType: 'pso_request',
                requestType: 'BAMS',
                requestCategory: formData.requestCategory,
                dateOfRequest: formData.dateOfRequest,
                branchCode: formData.branchCode,
                branchName: formData.branchName,
                branchId: formData.branchId,
                vendor: formData.vendor || '',
                vendorId: formData.vendorId || '',
                coveredArea: formData.coveredArea || '',
                floorsConsistOn: formData.floorsConsistOn || '',
                items: formData.items,
                remarks: formData.remarks,
                amount: formData.items.reduce((sum, item) => sum + (Number(item.totalAmount || (Number(item.amount) * Number(item.quantity))) || 0), 0),
                status: 'pending',
                isPOGenerated: false,
                createdBy: uid,
                psoId: psoDocId,
                psoName: pso?.name || 'PSO',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            await addDoc(collection(db, 'banks', companyId, 'hardware_requests'), newRequest);
            triggerToast('PSO Request created successfully', 'success');
            setShowAddModal(false);
            setFormData({
                dateOfRequest: new Date().toISOString().split('T')[0],
                branchCode: '',
                branchName: '',
                branchId: '',
                vendor: '',
                vendorId: '',
                coveredArea: '',
                floorsConsistOn: '',
                remarks: '',
                requestCategory: 'New',
                items: [],
                amount: '',
                requestType: 'BAMS'
            });
        } catch (error) {
            console.error("Error creating PSO request:", error);
            triggerToast('Failed to create PSO request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleUpdateStatus = async (req: PsoRequest, newStatus: 'forwarded' | 'approved' | 'rejected' | 'forwarded_to_regional') => {
        if (!companyId || isActionLoading) return;
        setIsActionLoading(true);
        try {
            const updateData: any = {
                status: newStatus,
                updatedAt: serverTimestamp()
            };
            if (newStatus === 'rejected') {
                updateData.rejectReason = rejectReason || window.prompt("Enter rejection reason (optional):") || '';
                updateData.rejectedBy = pso?.name || auth.currentUser?.email || 'Anonymous';
                updateData.rejectedByRole = designation || userRole || '';
            }
            if (newStatus === 'forwarded_to_regional') {
                updateData.inchargeRemarks = forwardRemarks || '';
            }

            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', req.id), updateData);
            triggerToast(`Request updated successfully`, 'success');
            setShowRejectModal(false);
            setRejectReason('');
            setForwardRemarks('');
            setShowDetailsModal(false);
        } catch (error) {
            console.error("Error updating status:", error);
            triggerToast('Failed to update request status', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleDeleteRequest = async (request: PsoRequest) => {
        if (!companyId || isActionLoading) return;
        if (!window.confirm("Are you sure you want to permanently delete this PSO Request?")) return;
        
        setIsActionLoading(true);
        try {
            await deleteDoc(doc(db, 'banks', companyId, 'hardware_requests', request.id));
            triggerToast("PSO Request deleted successfully", 'success');
            setShowDetailsModal(false);
        } catch (error) {
            console.error("Error deleting request:", error);
            triggerToast("Failed to delete PSO Request", 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const handleOpenResubmitModal = (request: PsoRequest) => {
        setResubmitRequestObj(request);
        setResubmitReasonDraft('');
        setResubmitType((request.requestType as any) === 'CCTV' ? 'CCTV' : 'BAMS');
        setShowResubmitModal(true);
    };

    const handleCloseResubmitModal = () => {
        setShowResubmitModal(false);
        setResubmitReasonDraft('');
        setResubmitRequestObj(null);
    };

    const handleConfirmResubmit = async () => {
        if (!companyId || !resubmitRequestObj || isActionLoading) return;

        setIsActionLoading(true);
        try {
            let newRequestId = resubmitRequestObj.requestId || '';
            if (resubmitType === 'CCTV') {
                newRequestId = newRequestId.startsWith('BAMS-RQ-')
                    ? newRequestId.replace('BAMS-RQ-', 'CCTV-RQ-')
                    : newRequestId.startsWith('CCTV-RQ-')
                        ? newRequestId
                        : `CCTV-RQ-${newRequestId}`;
            } else {
                newRequestId = newRequestId.startsWith('CCTV-RQ-')
                    ? newRequestId.replace('CCTV-RQ-', 'BAMS-RQ-')
                    : newRequestId.startsWith('BAMS-RQ-')
                        ? newRequestId
                        : `BAMS-RQ-${newRequestId}`;
            }

            const updateData: any = {
                status: 'pending',
                requestType: resubmitType,
                requestId: newRequestId,
                resubmitted: true,
                resubmissionReason: resubmitReasonDraft.trim(),
                resubmittedAt: serverTimestamp(),
                rejectReason: '',
                rejectedBy: '',
                rejectedByRole: '',
                updatedAt: serverTimestamp()
            };

            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', resubmitRequestObj.id), updateData);
            triggerToast('Request resubmitted successfully!', 'success');
            handleCloseResubmitModal();
            setShowDetailsModal(false);
        } catch (error) {
            console.error("Error resubmitting request:", error);
            triggerToast('Failed to resubmit request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleUpdateType = async (req: PsoRequest, newType: 'BAMS' | 'CCTV') => {
        if (!companyId || isActionLoading) return;
        if (!window.confirm(`Change request type to ${newType}? A new Request ID will be generated.`)) return;
        setIsActionLoading(true);
        try {
            let newRequestId = req.requestId || '';
            if (newType === 'CCTV') {
                newRequestId = newRequestId.startsWith('BAMS-RQ-')
                    ? newRequestId.replace('BAMS-RQ-', 'CCTV-RQ-')
                    : newRequestId.startsWith('CCTV-RQ-')
                        ? newRequestId
                        : `CCTV-RQ-${newRequestId}`;
            } else {
                newRequestId = newRequestId.startsWith('CCTV-RQ-')
                    ? newRequestId.replace('CCTV-RQ-', 'BAMS-RQ-')
                    : newRequestId.startsWith('BAMS-RQ-')
                        ? newRequestId
                        : `BAMS-RQ-${newRequestId}`;
            }
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', req.id), {
                requestType: newType,
                requestId: newRequestId,
                updatedAt: serverTimestamp()
            });
            triggerToast(`Type changed to ${newType} successfully`, 'success');
            setShowDetailsModal(false);
        } catch (error) {
            console.error('Error updating type:', error);
            triggerToast('Failed to update type', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    // Filter requests
    const filteredRequests = useMemo(() => {
        let list = [...requests];

        // Role-based visibility
        if (isPSO) {
            list = list.filter(r => r.createdBy === uid);
        } else if (isRegional) {
            list = list.filter(r => (r.createdBy === uid || managedPsoIds.includes(r.psoId)) && r.status !== 'pending');
        } else if (isHead) {
            list = list.filter(r => r.status === 'forwarded');
        } else if (isIncharge) {
            list = list.filter(r => r.status === 'pending' || r.status === 'approved' || r.status === 'forwarded_to_regional' || r.status === 'forwarded' || r.status === 'rejected');
        }

        // Search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r =>
                r.requestId.toLowerCase().includes(term) ||
                r.branchCode.toLowerCase().includes(term) ||
                r.branchName.toLowerCase().includes(term) ||
                r.psoName.toLowerCase().includes(term)
            );
        }

        // Status Filter
        if (statusFilter !== 'All') {
            list = list.filter(r => r.status === statusFilter);
        }

        return list;
    }, [requests, isPSO, isRegional, isHead, isIncharge, uid, managedPsoIds, searchTerm, statusFilter]);

    const mobileTotalPages = Math.ceil(filteredRequests.length / mobileItemsPerPage);
    const paginatedMobileRequests = useMemo(() => {
        const start = (mobilePage - 1) * mobileItemsPerPage;
        return filteredRequests.slice(start, start + mobileItemsPerPage);
    }, [filteredRequests, mobilePage]);

    // Branches filter for modal
    const filteredBranches = branches.filter(b => {
        if (!branchSearch) return true;
        const term = branchSearch.toLowerCase();
        return b.branchCode?.toLowerCase().includes(term) || b.name?.toLowerCase().includes(term) || b.address?.toLowerCase().includes(term);
    });

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'po created':
            case 'PO Created':
                return <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">PO Created</span>;
            case 'forwarded_to_regional':
                return <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">Forwarded to Regional</span>;
            case 'pending':
                return <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">Pending</span>;
            case 'forwarded':
                return <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">Forwarded</span>;
            case 'approved':
                return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Approved</span>;
            case 'rejected':
                return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Rejected</span>;
            default:
                return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800">{status}</span>;
        }
    };

    const handleViewPoDetail = (po: any) => {
        if (!po) {
            triggerToast('PO details are not available yet', 'warning');
            return;
        }
        setSelectedPo(po);
        setPoPsoStatus(po.psoStatus === 'installed' ? 'installed' : 'not installed');
        setPoInstallationDate(po.installationDate || new Date().toISOString().split('T')[0]);
        setShowPoModal(true);
    };

    const handleUpdatePoPsoStatus = async () => {
        if (!companyId || !selectedPo || !uid) return;

        setIsActionLoading(true);
        try {
            const updateData: any = {
                psoStatus: poPsoStatus,
                installationDate: poPsoStatus === 'installed' ? poInstallationDate : null,
                updatedAt: serverTimestamp(),
                updatedBy: uid
            };

            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedPo.id), updateData);
            triggerToast(`PO status updated to ${poPsoStatus === 'installed' ? 'Installed' : 'Not Installed'} successfully!`, 'success');
            
            setSelectedPo((prev: any) => prev ? {
                ...prev,
                psoStatus: poPsoStatus,
                installationDate: poPsoStatus === 'installed' ? poInstallationDate : null
            } : null);

        } catch (error) {
            console.error("Error updating PO PSO status:", error);
            triggerToast('Failed to update PO status', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && emailInput.trim()) {
            e.preventDefault();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const trimmedEmail = emailInput.trim();
            if (emailRegex.test(trimmedEmail)) {
                if (!emailRecipients.includes(trimmedEmail)) {
                    setEmailRecipients([...emailRecipients, trimmedEmail]);
                }
                setEmailInput('');
            } else {
                triggerToast('Please enter a valid email address', 'error');
            }
        }
    };

    const removeEmailRecipient = (emailToRemove: string) => {
        setEmailRecipients(emailRecipients.filter(email => email !== emailToRemove));
    };

    const formatPsoRequestEmail = (req: PsoRequest, vendorObj: any, bankName: string, subject: string, intro: string, outro: string, signature: string) => {
        const branchName = req.branchName || 'N/A';
        const branchCode = req.branchCode || 'N/A';
        const branchId = req.branchId || '';

        // Find branch in branches array to get address
        const branch = branches.find(b => b.id === req.branchId || b.branchCode === req.branchCode);
        const branchAddress = branch?.address || req.branchName || 'N/A';

        const items = req.items || [];
        const totalItems = items.length;
        const hasWallMount = totalItems > 0 && /wall\s*mount/i.test(items[totalItems - 1].itemName || '');

        const itemsTable = items.map((item, index) => {
            const hwInfo = item.specification ? `${item.itemName} (${item.specification})` : item.itemName;
            const qty = `${item.quantity} ${item.unit || 'piece'}`;

            const isLast = index === totalItems - 1;

            let makeModelText = "Make Model must as per approved SLA model";
            let amountText = `Rs. ${Number(item.totalAmount || (Number(item.amount) * Number(item.quantity)) || 0).toLocaleString()}`;

            if (hasWallMount && isLast) {
                makeModelText = "";
                amountText = "Charge as per actual";
            } else if (item.amount) {
                amountText = `Rs. ${Number(item.totalAmount || (Number(item.amount) * Number(item.quantity)) || 0).toLocaleString()}<br/><span style="font-size: 11px; color: #666;">(@ Rs. ${Number(item.amount).toLocaleString()}/${item.unit || 'piece'})</span>`;
            }

            return `
                <tr>
                    <td style="border: 1px solid #000000; padding: 10px; text-align: center; font-weight: bold; font-size: 13px; font-family: Calibri, Arial, sans-serif;">${index + 1}</td>
                    <td style="border: 1px solid #000000; padding: 10px; font-size: 13px; font-family: Calibri, Arial, sans-serif;"><strong>${hwInfo || 'N/A'}</strong></td>
                    <td style="border: 1px solid #000000; padding: 10px; text-align: center; font-weight: bold; font-size: 13px; font-family: Calibri, Arial, sans-serif;">${qty}</td>
                    <td style="border: 1px solid #000000; padding: 10px; text-align: center; font-size: 12px; color: #333; font-family: Calibri, Arial, sans-serif;">${makeModelText}</td>
                    <td style="border: 1px solid #000000; padding: 10px; text-align: center; font-size: 12px; color: #333; font-weight: bold; font-family: Calibri, Arial, sans-serif;">${amountText}</td>
                </tr>
            `;
        }).join('');

        const formattedIntro = marked.parse(intro || '', { breaks: true, gfm: true }) as string;
        const formattedOutro = marked.parse(outro || '', { breaks: true, gfm: true }) as string;
        const formattedSignature = marked.parse(signature || '', { breaks: true, gfm: true }) as string;

        const type = req.requestType || 'BAMS';

        const htmlBody = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${type} Hardware Request</title>
            <style>
                body { font-family: Calibri, Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f8f9fa; }
                .content { background: #ffffff; padding: 30px; border-radius: 8px; border: 1px solid #dee2e6; max-width: 800px; margin: 0 auto; box-sizing: border-box; }
                .intro { font-size: 14px; margin-bottom: 25px; color: #333; font-family: Calibri, Arial, sans-serif; }
                .table-container { margin: 20px 0; }
                .table { width: 100%; border-collapse: collapse; margin: 15px 0; border: 1px solid #000000; }
                .table th { background-color: #0b9496; color: white; border: 1px solid #000000; padding: 10px; text-align: center; font-size: 14px; font-weight: bold; font-family: Calibri, Arial, sans-serif; }
                .outro { font-size: 14px; margin: 25px 0; color: #333; font-family: Calibri, Arial, sans-serif; }
                .signature { font-size: 14px; color: #333; margin-top: 30px; line-height: 1.5; font-family: Calibri, Arial, sans-serif; }
            </style>
        </head>
        <body>
            <div class="content">
                <div class="intro">
                    ${formattedIntro}
                </div>
                
                <div class="table-container">
                    <table class="table">
                        <thead>
                            <tr>
                                <th style="width: 8%; text-align: center;">S. No.</th>
                                <th style="width: 50%;">Hardware Information</th>
                                <th style="width: 12%; text-align: center;">Quantity</th>
                                <th style="width: 15%; text-align: center;">Make/Model</th>
                                <th style="width: 15%; text-align: center;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${itemsTable}
                        </tbody>
                    </table>
                </div>
                
                <div class="outro">
                    ${formattedOutro}
                </div>
                
                <div class="signature">
                    ${formattedSignature}
                </div>
            </div>
        </body>
        </html>
        `;

        return htmlBody;
    };

    const handleSendEmail = async () => {
        if (!selectedRequest || !companyId) {
            triggerToast('Missing required information', 'error');
            return;
        }

        if (emailRecipients.length === 0) {
            triggerToast('Please add at least one recipient email address', 'error');
            return;
        }

        setIsSendingEmail(true);
        try {
            const vendorObj = vendors.find(v => v.companyName === selectedRequest.vendor || v.id === selectedRequest.vendorId || v.companyId === selectedRequest.vendorId);
            const bankName = bankDetails?.name || 'Bank';

            const emailBody = formatPsoRequestEmail(selectedRequest, vendorObj, bankName, emailSubject, emailIntro, emailOutro, emailSignature);
            const { sendEmailsWithErrorHandling } = await import('../../../../api/emailService');

            const isHblBank = companyId === 'hbl' || bankName.toLowerCase().includes('hbl');

            const emailPayload = {
                to: emailRecipients,
                subject: emailSubject,
                body: emailBody,
                isHtml: true,
                companyName: 'HBL',
            };

            console.log('[BAMSPsoRequest] Sending email with payload:', {
                to: emailPayload.to,
                subject: emailPayload.subject,
                companyName: emailPayload.companyName,
                bodyLength: emailPayload.body?.length,
                isHtml: emailPayload.isHtml,
            });

            const result = await sendEmailsWithErrorHandling(emailPayload);

            console.log('[BAMSPsoRequest] Email result:', result);

            if (result.successful.length > 0) {
                triggerToast('Email sent successfully', 'success');
                setShowEmailModal(false);
            } else {
                const errMsg = result.failed[0]?.error || 'Failed to send email';
                triggerToast(errMsg, 'error');
            }
        } catch (error: any) {
            console.error('Error sending email:', error);
            triggerToast(error.message || 'Failed to send email', 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const columns: Column<PsoRequest>[] = [
        { header: 'Request ID', accessor: 'requestId' },
        { header: 'Type', accessor: 'requestType', render: (r) => String(r.requestType) || 'BAMS' },
        { header: 'Date', accessor: 'dateOfRequest' },
        { header: 'Branch Code', accessor: 'branchCode' },
        { header: 'Branch Name', accessor: 'branchName' },
        { header: 'Amount', accessor: 'amount', render: (r) => r.amount ? `Rs. ${Number(r.amount).toLocaleString()}` : '-' },
        { header: 'PSO Name', accessor: 'psoName' },
        { header: 'Status', accessor: 'status', render: (r) => getStatusBadge(r.status) },
        {
            header: 'PO Generated',
            accessor: 'isPOGenerated',
            render: (r) => {
                if (r.isPOGenerated) {
                    const matchingPo = poRequests.find(po => po.referencedPsoRequestId === r.id);
                    const poText = matchingPo ? (matchingPo.poNumber || matchingPo.requestId || 'Yes') : 'Yes';
                    return (
                        <div className="flex flex-col">
                            <span className="text-green-600 dark:text-green-400 font-bold">Yes</span>
                            <button
                                onClick={() => handleViewPoDetail(matchingPo)}
                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold underline text-left focus:outline-none"
                            >
                                PO: {poText}
                            </button>
                        </div>
                    );
                }
                return <span className="text-gray-500 font-semibold">No</span>;
            }
        },
        {
            header: 'Actions',
            accessor: 'id',
            align: 'right',
            render: (r) => (
                <div className="flex justify-end gap-2">
                    {/* View Details */}
                    <div className="relative group flex items-center justify-center">
                        <button
                            onClick={() => {
                                setSelectedRequest(r);
                                setShowDetailsModal(true);
                            }}
                            className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                            <Icon icon={FiEye} className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                            View Details
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                        </div>
                    </div>

                    {/* Delete for Bank */}
                    {isBank && (
                        <div className="relative group flex items-center justify-center">
                            <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteRequest(r); }}
                                disabled={isActionLoading}
                                className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                title="Delete Request"
                            >
                                <Icon icon={FiTrash2} className="h-5 w-5" />
                            </button>
                            <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                Delete Request
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                            </div>
                        </div>
                    )}

                    {/* Incharge Actions in Table */}
                    {(isIncharge || isBank) && r.status === 'pending' && (
                        <>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => {
                                        setSelectedRequest(r);
                                        setShowDetailsModal(true);
                                        setForwardStep('ask_change');
                                    }}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-indigo-600 transition hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiCornerUpRight} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Forward to Regional
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => {
                                        setSelectedRequest(r);
                                        setRejectReason('');
                                        setShowRejectModal(true);
                                    }}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiX} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Reject
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Regional Actions in Table */}
                    {(isRegional || isBank) && r.status === 'forwarded_to_regional' && (
                        <>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => handleUpdateStatus(r, 'forwarded')}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-purple-600 transition hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiCornerUpRight} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Forward to Head
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => {
                                        setSelectedRequest(r);
                                        setRejectReason('');
                                        setShowRejectModal(true);
                                    }}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiX} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Reject
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Head Actions in Table */}
                    {(isHead || isBank) && r.status === 'forwarded' && (
                        <>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => handleUpdateStatus(r, 'approved')}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-green-600 transition hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiCheck} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Approve
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                            <div className="relative group flex items-center justify-center">
                                <button
                                    onClick={() => {
                                        setSelectedRequest(r);
                                        setRejectReason('');
                                        setShowRejectModal(true);
                                    }}
                                    disabled={isActionLoading}
                                    className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                >
                                    <Icon icon={FiX} className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                    Reject
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                                </div>
                            </div>
                        </>
                    )}

                    {isIncharge && r.status === 'approved' && !r.isPOGenerated && (
                        <div className="relative group flex items-center justify-center">
                            <button
                                onClick={() => onInitiatePO(r)}
                                className="rounded-lg p-2 text-green-600 transition hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                            >
                                <Icon icon={FiCornerUpRight} className="h-5 w-5" />
                            </button>
                            <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                Generate PO Request
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                            </div>
                        </div>
                    )}

                    {(isPSO || isBank) && r.status === 'approved' && (
                        <div className="relative group flex items-center justify-center">
                            <button
                                onClick={() => {
                                    initEmailComposer(r);
                                }}
                                className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                            >
                                <Icon icon={FiMail} className="h-5 w-5" />
                            </button>
                            <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 pointer-events-none whitespace-nowrap rounded-lg bg-gray-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl dark:bg-gray-800 border border-gray-800 dark:border-gray-700">
                                Send Email
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950 dark:border-t-gray-800"></div>
                            </div>
                        </div>
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            {!hideList && (
                <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        {!summaryMode && (
                            <TextField
                                type="text"
                                placeholder="Search PSO request..."
                                value={searchTerm}
                                onChange={(e: any) => setSearchTerm(e.target.value)}
                                prefix={<Icon icon={FiSearch} className="h-4 w-4" />}
                            />
                        )}
                        {!summaryMode && !isHead && !isIncharge && (
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            >
                                <option value="All">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="forwarded_to_regional">Forwarded to Regional</option>
                                <option value="forwarded">Forwarded</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        )}
                    </div>
                    {isPSO && (
                        <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                            <Icon icon={FiPlus} className="h-4 w-4" />
                            Create PSO Request
                        </Button>
                    )}
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                    <Table
                        data={filteredRequests}
                        columns={columns}
                        keyExtractor={(r) => r.id}
                        loading={loading}
                        emptyMessage="No PSO Requests found"
                    />
                </div>

                {/* Mobile View */}
                <div className="block md:hidden space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
                            </div>
                        </div>
                    ) : paginatedMobileRequests.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400 dark:bg-black/30">
                            No PSO Requests found
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4">
                                {paginatedMobileRequests.map((r) => {
                                    const matchingPo = poRequests.find(po => po.referencedPsoRequestId === r.id);
                                    const poText = matchingPo ? (matchingPo.poNumber || matchingPo.requestId || 'Yes') : 'Yes';

                                    return (
                                        <div
                                            key={r.id}
                                            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-800/40 space-y-3"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                                    {r.requestId}
                                                </span>
                                                {getStatusBadge(r.status)}
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">Type</span>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                        {String(r.requestType) || 'BAMS'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">Date</span>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{r.dateOfRequest}</span>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">Branch</span>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                        {r.branchCode} - {r.branchName}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">Amount</span>
                                                    <span className="font-bold text-gray-900 dark:text-gray-100">
                                                        {r.amount ? `Rs. ${Number(r.amount).toLocaleString()}` : '-'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">PO Generated</span>
                                                    {r.isPOGenerated ? (
                                                        <div className="flex flex-col">
                                                            <span className="text-green-600 dark:text-green-400 font-bold">Yes</span>
                                                            <button
                                                                onClick={() => handleViewPoDetail(matchingPo)}
                                                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold underline text-left focus:outline-none"
                                                            >
                                                                PO: {poText}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-500 dark:text-gray-400 font-semibold">No</span>
                                                    )}
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="block text-gray-500 dark:text-gray-400 font-medium">PSO Name</span>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{r.psoName}</span>
                                                </div>
                                            </div>

                                            <div className="border-t border-gray-100 dark:border-gray-700/50 pt-3 flex justify-end gap-2">
                                                {/* View Details */}
                                                <button
                                                    onClick={() => {
                                                        setSelectedRequest(r);
                                                        setShowDetailsModal(true);
                                                    }}
                                                    className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                                    title="View Details"
                                                >
                                                    <Icon icon={FiEye} className="h-5 w-5" />
                                                </button>

                                                {/* Delete for Bank */}
                                                {isBank && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteRequest(r); }}
                                                        disabled={isActionLoading}
                                                        className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                                        title="Delete Request"
                                                    >
                                                        <Icon icon={FiTrash2} className="h-5 w-5" />
                                                    </button>
                                                )}

                                                {/* Regional Actions */}
                                                {(isRegional || isBank) && r.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleUpdateStatus(r, 'forwarded')}
                                                            disabled={isActionLoading}
                                                            className="rounded-lg p-2 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 disabled:opacity-50"
                                                            title="Forward to Head"
                                                        >
                                                            <Icon icon={FiCornerUpRight} className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                    setSelectedRequest(r);
                                                                    setRejectReason('');
                                                                    setShowRejectModal(true);
                                                            }}
                                                            disabled={isActionLoading}
                                                            className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                                            title="Reject"
                                                        >
                                                            <Icon icon={FiX} className="h-5 w-5" />
                                                        </button>
                                                    </>
                                                )}

                                                {/* Head Actions */}
                                                {(isHead || isBank) && r.status === 'forwarded' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleUpdateStatus(r, 'approved')}
                                                            disabled={isActionLoading}
                                                            className="rounded-lg p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 disabled:opacity-50"
                                                            title="Approve"
                                                        >
                                                            <Icon icon={FiCheck} className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                    setSelectedRequest(r);
                                                                    setRejectReason('');
                                                                    setShowRejectModal(true);
                                                            }}
                                                            disabled={isActionLoading}
                                                            className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                                                            title="Reject"
                                                        >
                                                            <Icon icon={FiX} className="h-5 w-5" />
                                                        </button>
                                                    </>
                                                )}

                                                {isIncharge && r.status === 'approved' && !r.isPOGenerated && (
                                                    <button
                                                        onClick={() => onInitiatePO(r)}
                                                        className="rounded-lg p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                                                        title="Generate PO Request"
                                                    >
                                                        <Icon icon={FiCornerUpRight} className="h-5 w-5" />
                                                    </button>
                                                )}

                                                {(isPSO || isBank) && r.status === 'approved' && (
                                                    <button
                                                        onClick={() => {
                                                            initEmailComposer(r);
                                                        }}
                                                        className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                                        title="Send Email"
                                                    >
                                                        <Icon icon={FiMail} className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination for Mobile Cards */}
                            {mobileTotalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setMobilePage(p => Math.max(1, p - 1))}
                                        disabled={mobilePage === 1}
                                        className="px-3 py-1.5 text-xs"
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                        Page {mobilePage} of {mobileTotalPages}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setMobilePage(p => Math.min(mobileTotalPages, p + 1))}
                                        disabled={mobilePage === mobileTotalPages}
                                        className="px-3 py-1.5 text-xs"
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        )}

            {/* Add Request Modal */}
            {showAddModal && (
                <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="New PSO Hardware Request" size="xl" closeOnBackdropClick={false}>
                    <div className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Request</label>
                                <TextField
                                    type="date"
                                    value={formData.dateOfRequest}
                                    onChange={(e: any) => setFormData(prev => ({ ...prev, dateOfRequest: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Category</label>
                                <select
                                    value={formData.requestCategory}
                                    onChange={(e: any) => setFormData(prev => ({ ...prev, requestCategory: e.target.value }))}
                                    className="form-control h-9 py-0 w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-medium text-gray-900 dark:text-gray-100"
                                >
                                    <option value="New">New</option>
                                    <option value="Replacement">Replacement</option>
                                    <option value="Additional">Additional</option>
                                </select>
                            </div>
                            <div className="relative">
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                <TextField
                                    type="text"
                                    value={formData.branchCode}
                                    placeholder="Click to select branch"
                                    readOnly
                                    onClick={() => setShowBranchModal(true)}
                                    suffix={<Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                />
                                {showBranchModal && (
                                    <>
                                        <DropdownBackdrop onClose={() => { setShowBranchModal(false); setBranchSearch(''); }} />
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search branch..."
                                                    value={branchSearch}
                                                    onChange={(e: any) => setBranchSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {filteredBranches.map(branch => (
                                                    <div
                                                        key={branch.id}
                                                        onClick={() => handleBranchSelect(branch)}
                                                        className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                    >
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                <TextField
                                    type="text"
                                    value={formData.items.reduce((sum, item) => sum + (Number(item.totalAmount || (Number(item.amount) * Number(item.quantity))) || 0), 0).toLocaleString()}
                                    readOnly
                                    placeholder="Total Amount"
                                    prefix={
                                        <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                            Rs.
                                        </span>
                                    }
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                <TextField type="text" value={formData.branchName} readOnly />
                            </div>
                            <div className="relative">
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor *</label>
                                <TextField
                                    type="text"
                                    value={formData.vendor}
                                    placeholder="Click to select vendor"
                                    readOnly
                                    onClick={() => setShowVendorModal(true)}
                                    required
                                    suffix={<Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                />
                                {showVendorModal && (
                                    <>
                                        <DropdownBackdrop onClose={() => { setShowVendorModal(false); setVendorSearch(''); }} />
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search vendor..."
                                                    value={vendorSearch}
                                                    onChange={(e: any) => setVendorSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {vendors.filter(v => (v.companyName || '').toLowerCase().includes(vendorSearch.toLowerCase())).length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                                ) : (
                                                    vendors.filter(v => (v.companyName || '').toLowerCase().includes(vendorSearch.toLowerCase())).map(vendor => (
                                                        <div
                                                            key={vendor.id}
                                                            onClick={() => handleVendorSelect(vendor)}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Floor Consist On</label>
                                <TextField
                                    type="text"
                                    value={formData.floorsConsistOn}
                                    onChange={(e: any) => setFormData(prev => ({ ...prev, floorsConsistOn: e.target.value }))}
                                    placeholder="e.g. Ground + First Floor"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Covered Area</label>
                                <TextField
                                    type="text"
                                    value={formData.coveredArea}
                                    onChange={(e: any) => {
                                        const clean = e.target.value.replace(/[^\d]/g, '');
                                        setFormData(prev => ({
                                            ...prev,
                                            coveredArea: clean ? Number(clean).toLocaleString() : ''
                                        }));
                                    }}
                                    placeholder="e.g. 100,000"
                                    suffix={
                                        <span className="absolute right-[-6px] whitespace-nowrap inline-flex items-center rounded bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/30 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider select-none">
                                            Square Feet
                                        </span>
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                            <MarkdownEditor
                                value={formData.remarks}
                                onChange={(val) => setFormData(prev => ({ ...prev, remarks: val }))}
                                placeholder="Enter any instructions or remarks... Use toolbar to format."
                                rows={3}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requested Items</label>
                                <Button variant="secondary" onClick={() => setShowAddItemModal(true)}>
                                    <Icon icon={FiPlus} className="mr-1 h-4 w-4" /> Add Item
                                </Button>
                            </div>
                            {formData.items.length === 0 ? (
                                <p className="text-sm text-gray-500">No items added yet.</p>
                            ) : (
                                <div className="border rounded-xl overflow-x-auto dark:border-gray-700">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 border-b dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                                                <th className="p-3">Item Name / Spec</th>
                                                <th className="p-3">Type</th>
                                                <th className="p-3 text-center">Qty & Est. Total</th>
                                                <th className="p-3">Location</th>
                                                <th className="p-3">Coverage</th>
                                                <th className="p-3 text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, idx) => (
                                                <tr key={idx} className="border-b last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:border-gray-700 text-sm">
                                                    <td className="p-3 font-medium text-gray-900 dark:text-gray-100">
                                                        {item.itemName}
                                                        <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                            {item.specification ? `${item.specification} ` : ''}@ Rs. {Number(item.amount || 0).toLocaleString()}/{item.unit || 'piece'}
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.requirementType === 'Additional'
                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                                                            }`}>
                                                            {item.requirementType || 'Complaince'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <div className="font-bold">{item.quantity} {item.unit || 'piece'}</div>
                                                        <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                            Rs. {Number(item.totalAmount || (Number(item.amount) * Number(item.quantity)) || 0).toLocaleString()}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-gray-600 dark:text-gray-400">{item.location || '-'}</td>
                                                    <td className="p-3 text-gray-600 dark:text-gray-400">{item.coverageDetail || '-'}</td>
                                                    <td className="p-3 text-right">
                                                        <button type="button" onClick={() => handleRemoveItem(idx)} className="text-red-500 hover:text-red-700">
                                                            <Icon icon={FiX} className="h-5 w-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleSaveRequest} disabled={isActionLoading}>
                                {isActionLoading ? 'Creating...' : 'Submit Request'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Item Sub-Modal */}
            {showAddItemModal && (
                <Modal
                    isOpen={showAddItemModal}
                    onClose={() => setShowAddItemModal(false)}
                    title="Add Item Specification"
                    size="md"
                    overlayClassName="z-[60]"
                    closeOnBackdropClick={false}
                    footer={
                        <div className="flex justify-end gap-2 px-6 py-4">
                            <Button variant="secondary" onClick={() => setShowAddItemModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleAddItem}>Add Item</Button>
                        </div>
                    }
                >
                    <div className="px-6 py-5 space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Select Item *</label>
                            <SearchableItemSelect
                                items={filteredInventoryItems}
                                value={selectedInventoryItemId}
                                onChange={(inv) => {
                                    setSelectedInventoryItemId(inv.id);
                                    setNewItem(prev => ({
                                        ...prev,
                                        itemId: inv.id,
                                        itemCode: inv.itemCode || '',
                                        itemName: inv.itemCode ? `${inv.itemCode} - ${inv.itemName}` : (inv.itemName || ''),
                                        specification: inv.itemDescription || '',
                                        amount: String(inv.rates || 0),
                                        totalAmount: (inv.rates || 0) * (prev.quantity || 1)
                                    }));
                                }}
                                placeholder="Choose Item..."
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Specification</label>
                            <TextField
                                type="text"
                                value={newItem.specification}
                                onChange={() => {}}
                                placeholder="-"
                                readOnly={true}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Quantity</label>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewItem(prev => ({ ...prev, quantity: Math.max(1, (Number(prev.quantity) || 1) - 1) }))}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 hover:bg-gray-200 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 select-none focus:outline-none transition-all"
                                    >
                                        <Icon icon={FiMinus} className="h-4 w-4" />
                                    </button>
                                    <TextField
                                        type="number"
                                        value={newItem.quantity}
                                        onChange={(e: any) => {
                                            const val = e.target.value;
                                            if (val === '') {
                                                setNewItem(prev => ({ ...prev, quantity: '' as any }));
                                            } else {
                                                const parsed = parseInt(val);
                                                setNewItem(prev => ({ ...prev, quantity: isNaN(parsed) ? 1 : Math.max(1, parsed) }));
                                            }
                                        }}
                                        onBlur={(e: any) => {
                                            if (!e.target.value || parseInt(e.target.value) < 1) {
                                                setNewItem(prev => ({ ...prev, quantity: 1 }));
                                            }
                                        }}
                                        onFocus={(e: any) => e.target.select()}
                                        onClick={(e: any) => e.target.select()}
                                        min="1"
                                        className="text-center w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setNewItem(prev => ({ ...prev, quantity: (Number(prev.quantity) || 1) + 1 }))}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 hover:bg-gray-200 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 select-none focus:outline-none transition-all"
                                    >
                                        <Icon icon={FiPlus} className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Unit *</label>
                                <select
                                    value={newItem.unit || 'piece'}
                                    onChange={(e: any) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                                    className="form-control h-9 py-0 w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-medium text-gray-950 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-700 px-3"
                                >
                                    <option value="piece">piece</option>
                                    <option value="meter">meter</option>
                                    <option value="feet">feet</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Amount (Per Unit) *</label>
                                <TextField
                                    type="text"
                                    value={newItem.amount ? Number(newItem.amount).toLocaleString() : ''}
                                    onChange={() => {}}
                                    placeholder="-"
                                    readOnly={true}
                                    prefix={
                                        <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                            Rs.
                                        </span>
                                    }
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                <TextField
                                    type="text"
                                    value={((Number(newItem.amount) || 0) * (Number(newItem.quantity) || 1)).toLocaleString()}
                                    readOnly
                                    placeholder="Rs. 0"
                                    prefix={
                                        <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                            Rs.
                                        </span>
                                    }
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Location / Floor</label>
                                <TextField
                                    type="text"
                                    value={newItem.location}
                                    onChange={(e: any) => setNewItem(prev => ({ ...prev, location: e.target.value }))}
                                    placeholder="e.g. Ground Floor, Cash Counter"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Coverage Detail / Area</label>
                                <TextField
                                    type="text"
                                    value={newItem.coverageDetail}
                                    onChange={(e: any) => setNewItem(prev => ({ ...prev, coverageDetail: e.target.value }))}
                                    placeholder="e.g. Main Entrance Gate Area"
                                />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Details/Approval Modal */}
            {showDetailsModal && selectedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setIsEditingDetails(false);
                    }}
                    title={isEditingDetails ? `Edit PSO Request - ${selectedRequest.requestId}` : `PSO Request - ${selectedRequest.requestId}`}
                    size="xl"
                    closeOnBackdropClick={false}
                >
                    {isEditingDetails ? (
                        /* EDIT MODE FOR DETAILS MODAL */
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Request</label>
                                    <TextField
                                        type="date"
                                        value={editFormData.dateOfRequest}
                                        onChange={(e: any) => setEditFormData(prev => ({ ...prev, dateOfRequest: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Category</label>
                                    <select
                                        value={editFormData.requestCategory}
                                        onChange={(e: any) => setEditFormData(prev => ({ ...prev, requestCategory: e.target.value }))}
                                        className="form-control h-9 py-0 w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-medium text-gray-900 dark:text-gray-100"
                                    >
                                        <option value="New">New</option>
                                        <option value="Replacement">Replacement</option>
                                        <option value="Additional">Additional</option>
                                    </select>
                                </div>
                                <div className="relative">
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <TextField
                                        type="text"
                                        value={editFormData.branchCode}
                                        placeholder="Click to select branch"
                                        readOnly
                                        onClick={() => setShowEditBranchModal(true)}
                                        suffix={<Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                    />
                                    {showEditBranchModal && (
                                        <>
                                            <DropdownBackdrop onClose={() => { setShowEditBranchModal(false); setEditBranchSearch(''); }} />
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                    <TextField
                                                        type="text"
                                                        placeholder="Search branch..."
                                                        value={editBranchSearch}
                                                        onChange={(e: any) => setEditBranchSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="max-h-48 overflow-y-auto">
                                                    {branches.filter(b => {
                                                        if (!editBranchSearch) return true;
                                                        const term = editBranchSearch.toLowerCase();
                                                        return b.branchCode?.toLowerCase().includes(term) || b.name?.toLowerCase().includes(term) || b.address?.toLowerCase().includes(term);
                                                    }).map(branch => (
                                                        <div
                                                            key={branch.id}
                                                            onClick={() => handleEditBranchSelect(branch)}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                    <TextField
                                        type="text"
                                        value={editFormData.items.reduce((sum, item) => sum + (Number(item.totalAmount || (Number(item.amount) * Number(item.quantity))) || 0), 0).toLocaleString()}
                                        readOnly
                                        placeholder="Total Amount"
                                        prefix={
                                            <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                                Rs.
                                            </span>
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <TextField type="text" value={editFormData.branchName} readOnly />
                                </div>
                                <div className="relative">
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor *</label>
                                    <TextField
                                        type="text"
                                        value={editFormData.vendor}
                                        placeholder="Click to select vendor"
                                        readOnly
                                        onClick={() => setShowEditVendorModal(true)}
                                        required
                                        suffix={<Icon icon={FiChevronDown} className="h-4 w-4 text-gray-500" />}
                                    />
                                    {showEditVendorModal && (
                                        <>
                                            <DropdownBackdrop onClose={() => { setShowEditVendorModal(false); setEditVendorSearch(''); }} />
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                    <TextField
                                                        type="text"
                                                        placeholder="Search vendor..."
                                                        value={editVendorSearch}
                                                        onChange={(e: any) => setEditVendorSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="max-h-48 overflow-y-auto">
                                                    {vendors.filter(v => (v.companyName || '').toLowerCase().includes(editVendorSearch.toLowerCase())).length === 0 ? (
                                                        <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                                    ) : (
                                                        vendors.filter(v => (v.companyName || '').toLowerCase().includes(editVendorSearch.toLowerCase())).map(vendor => (
                                                            <div
                                                                key={vendor.id}
                                                                onClick={() => handleEditVendorSelect(vendor)}
                                                                className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                            >
                                                                <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Floor Consist On</label>
                                    <TextField
                                        type="text"
                                        value={editFormData.floorsConsistOn}
                                        onChange={(e: any) => setEditFormData(prev => ({ ...prev, floorsConsistOn: e.target.value }))}
                                        placeholder="e.g. Ground + First Floor"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Covered Area</label>
                                    <TextField
                                        type="text"
                                        value={editFormData.coveredArea}
                                        onChange={(e: any) => {
                                            const clean = e.target.value.replace(/[^\d]/g, '');
                                            setEditFormData(prev => ({
                                                ...prev,
                                                coveredArea: clean ? Number(clean).toLocaleString() : ''
                                            }));
                                        }}
                                        placeholder="e.g. 100,000"
                                        suffix={
                                            <span className="absolute right-[-6px] whitespace-nowrap inline-flex items-center rounded bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/30 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider select-none">
                                                Square Feet
                                            </span>
                                        }
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                                <MarkdownEditor
                                    value={editFormData.remarks}
                                    onChange={(val) => setEditFormData(prev => ({ ...prev, remarks: val }))}
                                    placeholder="Enter any instructions or remarks... Use toolbar to format."
                                    rows={3}
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requested Items</label>
                                    <Button variant="secondary" onClick={() => setShowEditAddItemModal(true)}>
                                        <Icon icon={FiPlus} className="mr-1 h-4 w-4" /> Add Item
                                    </Button>
                                </div>
                                {editFormData.items.length === 0 ? (
                                    <p className="text-sm text-gray-500">No items added yet.</p>
                                ) : (
                                    <div className="border rounded-xl overflow-x-auto dark:border-gray-700">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 border-b dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                                                    <th className="p-3">Item Name / Spec</th>
                                                    <th className="p-3">Requirement Type</th>
                                                    <th className="p-3 text-center">Qty & Est. Total</th>
                                                    <th className="p-3">Location / Floor</th>
                                                    <th className="p-3">Coverage Detail / Area</th>
                                                    <th className="p-3 text-right">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editFormData.items.map((item, idx) => (
                                                    <tr key={idx} className="border-b last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:border-gray-700 text-sm">
                                                        <td className="p-3 font-medium text-gray-900 dark:text-gray-100">
                                                            {item.itemName}
                                                            <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                                {item.specification ? `${item.specification} ` : ''}@ Rs. {Number(item.amount || 0).toLocaleString()}/{item.unit || 'piece'}
                                                            </div>
                                                        </td>
                                                        <td className="p-3">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.requirementType === 'Additional'
                                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                                                : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                                                                }`}>
                                                                {item.requirementType || 'Complaince'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center">
                                                             <div className="flex items-center justify-center gap-1.5">
                                                                 <input
                                                                     type="number"
                                                                     min="1"
                                                                     value={item.quantity}
                                                                     onChange={(e) => {
                                                                         const val = Math.max(1, parseInt(e.target.value) || 1);
                                                                         const updatedItems = [...editFormData.items];
                                                                         updatedItems[idx] = {
                                                                             ...item,
                                                                             quantity: val,
                                                                             totalAmount: val * Number(item.amount || 0)
                                                                         };
                                                                         setEditFormData(prev => ({
                                                                             ...prev,
                                                                             items: updatedItems
                                                                         }));
                                                                     }}
                                                                     className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-center text-sm font-semibold focus:border-blue-500 focus:outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 bg-white"
                                                                 />
                                                                 <span className="text-xs text-gray-500 dark:text-gray-400">{item.unit || 'piece'}</span>
                                                             </div>
                                                             <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                                 Rs. {Number(item.totalAmount || (Number(item.amount) * Number(item.quantity)) || 0).toLocaleString()}
                                                             </div>
                                                         </td>
                                                        <td className="p-3 text-gray-600 dark:text-gray-400">{item.location || '-'}</td>
                                                        <td className="p-3 text-gray-600 dark:text-gray-400">{item.coverageDetail || '-'}</td>
                                                        <td className="p-3 text-right">
                                                            <button type="button" onClick={() => handleEditRemoveItem(idx)} className="text-red-500 hover:text-red-700">
                                                                <Icon icon={FiX} className="h-5 w-5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t dark:border-gray-700">
                                {isIncharge && editForwardStep === 'show_remarks' ? (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Remarks for Regional Head / Regional PSO (Optional)
                                            </label>
                                            <MarkdownEditor
                                                value={forwardRemarks}
                                                onChange={(val) => setForwardRemarks(val)}
                                                placeholder="Type remarks here..."
                                                rows={3}
                                            />
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                variant="secondary"
                                                onClick={() => setEditForwardStep('none')}
                                            >
                                                Back to Edit
                                            </Button>
                                            <Button
                                                variant="primary"
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                                onClick={handleSaveAndForward}
                                                disabled={isActionLoading}
                                            >
                                                {isActionLoading ? 'Submitting...' : 'Submit & Forward'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-end gap-3">
                                        <Button variant="secondary" onClick={() => { setIsEditingDetails(false); setEditForwardStep('none'); }}>Cancel</Button>
                                        {isIncharge ? (
                                            <Button
                                                variant="primary"
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                                onClick={() => {
                                                    setForwardRemarks(computeAutoRemarks(originalRequestItems, editFormData.items));
                                                    setEditForwardStep('show_remarks');
                                                }}
                                                disabled={isActionLoading}
                                              >
                                                Forward to Regional
                                            </Button>
                                        ) : (
                                            <Button variant="primary" onClick={handleSaveEdit} disabled={isActionLoading}>
                                                {isActionLoading ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* READ-ONLY VIEW MODE (EXISTING LAYOUT) */
                        <div className="p-6 space-y-4 bg-white dark:bg-gray-900">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <TextField type="text" value={selectedRequest.requestId} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Type</label>
                                    <div className="h-9 flex items-center">
                                        {isBank ? (
                                            <select
                                                value={(selectedRequest.requestType as any) || 'BAMS'}
                                                onChange={(e) => handleUpdateType(selectedRequest!, e.target.value as 'BAMS' | 'CCTV')}
                                                disabled={isActionLoading}
                                                className="rounded-xl border border-gray-300 bg-white py-1.5 px-3 text-sm font-semibold text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                            >
                                                <option value="BAMS">BAMS</option>
                                                <option value="CCTV">CCTV</option>
                                            </select>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{(selectedRequest.requestType as any) || 'BAMS'}</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Request</label>
                                    <TextField type="text" value={selectedRequest.dateOfRequest || '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Category</label>
                                    <TextField type="text" value={selectedRequest.requestCategory || 'New'} readOnly />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <TextField type="text" value={selectedRequest.branchCode || '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <TextField type="text" value={selectedRequest.branchName || '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    <div className="h-9 flex items-center">
                                        {isBank ? (
                                            <select
                                                value={selectedRequest.status}
                                                onChange={(e) => handleUpdateStatus(selectedRequest, e.target.value as any)}
                                                disabled={isActionLoading}
                                                className="rounded-xl border border-gray-300 bg-white py-1.5 px-3 text-sm font-semibold text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="forwarded_to_regional">Forwarded to Regional</option>
                                                <option value="forwarded">Forwarded to Head</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        ) : (
                                            getStatusBadge(selectedRequest.status)
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                    <TextField type="text" value={selectedRequest.amount ? `Rs. ${Number(selectedRequest.amount).toLocaleString()}` : '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Name</label>
                                    <TextField type="text" value={selectedRequest.psoName || '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor</label>
                                    <TextField type="text" value={selectedRequest.vendor || '-'} readOnly />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Floor Consist On</label>
                                    <TextField type="text" value={selectedRequest.floorsConsistOn || '-'} readOnly />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Covered Area</label>
                                    <TextField
                                        type="text"
                                        value={selectedRequest.coveredArea || '-'}
                                        readOnly
                                        suffix={
                                            selectedRequest.coveredArea ? (
                                                <span className="absolute right-[-6px] whitespace-nowrap inline-flex items-center rounded bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/30 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider select-none">
                                                    Square Feet
                                                </span>
                                            ) : undefined
                                        }
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Requested Items</label>
                                <div className="overflow-x-auto border rounded-xl dark:border-gray-700">
                                    <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-800">
                                            <th className="border px-3 py-2 text-left text-xs font-semibold">Item Name / Spec</th>
                                            <th className="border px-3 py-2 text-left text-xs font-semibold">Type</th>
                                            <th className="border px-3 py-2 text-center text-xs font-semibold">Qty & Est. Total</th>
                                            <th className="border px-3 py-2 text-left text-xs font-semibold">Location</th>
                                            <th className="border px-3 py-2 text-left text-xs font-semibold">Coverage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedRequest.items.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                <td className="border px-3 py-2 text-sm font-medium">
                                                    {item.itemName}
                                                    <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                        {item.specification ? `${item.specification} ` : ''}@ Rs. {Number(item.amount || 0).toLocaleString()}/{item.unit || 'piece'}
                                                    </div>
                                                </td>
                                                <td className="border px-3 py-2 text-sm">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.requirementType === 'Additional'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                                        : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                                                        }`}>
                                                        {item.requirementType || 'Complaince'}
                                                    </span>
                                                </td>
                                                <td className="border px-3 py-2 text-sm text-center">
                                                    <div className="font-bold">{item.quantity} {item.unit || 'piece'}</div>
                                                    <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                        Rs. {Number(item.totalAmount || (Number(item.amount) * Number(item.quantity)) || 0).toLocaleString()}
                                                    </div>
                                                </td>
                                                <td className="border px-3 py-2 text-sm">{item.location || '-'}</td>
                                                <td className="border px-3 py-2 text-sm">{item.coverageDetail || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                                <div className="w-full rounded-xl border border-gray-200 p-3 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 bg-gray-50/50 min-h-[3rem] overflow-y-auto max-h-[10rem]">
                                    <Markdown content={selectedRequest.remarks || '-'} />
                                </div>
                            </div>

                            {selectedRequest.rejectReason && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-red-700 dark:text-red-400">
                                        {selectedRequest.rejectedBy 
                                            ? `Rejected By: ${selectedRequest.rejectedBy} (${selectedRequest.rejectedByRole || 'N/A'})` 
                                            : 'Rejection Reason'}
                                    </label>
                                    <div className="w-full rounded-xl border border-red-300 p-3 text-sm dark:bg-red-950/20 dark:text-red-300 dark:border-red-800 bg-red-50/50 overflow-y-auto max-h-[10rem] text-red-900">
                                        <Markdown content={selectedRequest.rejectReason} />
                                    </div>
                                </div>
                            )}

                            {selectedRequest.resubmitted && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmitted</label>
                                        <div className="h-9 flex items-center">
                                            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">Yes</span>
                                        </div>
                                    </div>
                                    {selectedRequest.resubmittedAt && (
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmitted Date</label>
                                            <TextField type="text" value={formatDateTime(selectedRequest.resubmittedAt)} readOnly />
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedRequest.resubmitted && selectedRequest.resubmissionReason && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmission Reason</label>
                                    <div className="w-full rounded-xl border border-gray-200 p-3 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 bg-gray-50/50 overflow-y-auto max-h-[10rem]">
                                        <Markdown content={selectedRequest.resubmissionReason} />
                                    </div>
                                </div>
                            )}

                            {selectedRequest.inchargeRemarks && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Incharge Remarks</label>
                                    <div className="w-full rounded-xl border border-gray-200 p-3 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 bg-gray-50/50 overflow-y-auto max-h-[10rem]">
                                        <Markdown content={selectedRequest.inchargeRemarks} />
                                    </div>
                                </div>
                            )}

                            {(isIncharge || isBank) && selectedRequest.status === 'pending' && forwardStep === 'ask_change' && (
                                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/40 dark:bg-blue-950/20 space-y-2">
                                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                                        Forward Request to Regional Head / Regional PSO
                                    </h4>
                                    <p className="text-xs text-blue-800 dark:text-blue-300">
                                        Is the current request good to go, or would you like to make changes to items/quantities first?
                                    </p>
                                    <div className="flex gap-2 pt-2">
                                        <Button
                                            variant="primary"
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                            onClick={() => {
                                                setForwardStep('show_remarks');
                                                setForwardRemarks(computeAutoRemarks(originalRequestItems, selectedRequest.items));
                                            }}
                                        >
                                            Yes, Request is Good
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            className="bg-indigo-50 border-indigo-200 hover:bg-indigo-100 text-indigo-700 text-xs"
                                            onClick={() => {
                                                setForwardStep('none');
                                                handleStartEditing();
                                            }}
                                        >
                                            Change Request
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            className="text-xs"
                                            onClick={() => setForwardStep('none')}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {(isIncharge || isBank) && selectedRequest.status === 'pending' && forwardStep === 'show_remarks' && (
                                <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 dark:border-indigo-800/40 dark:bg-indigo-950/10 space-y-3">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Remarks for Regional Head / Regional PSO (Optional)
                                        </label>
                                        <MarkdownEditor
                                            value={forwardRemarks}
                                            onChange={(val) => setForwardRemarks(val)}
                                            placeholder="Type remarks here..."
                                            rows={4}
                                        />
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setForwardStep('none')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                            onClick={() => handleUpdateStatus(selectedRequest, 'forwarded_to_regional')}
                                            disabled={isActionLoading}
                                        >
                                            {isActionLoading ? 'Submitting...' : 'Submit & Forward'}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-4 border-t dark:border-gray-700">
                                <div>
                                    {forwardStep === 'none' && isIncharge && selectedRequest.status === 'approved' && !selectedRequest.isPOGenerated && (
                                        <Button variant="primary" onClick={() => { setShowDetailsModal(false); onInitiatePO(selectedRequest); }} className="flex items-center gap-2">
                                            <Icon icon={FiCornerUpRight} className="h-4 w-4" />
                                            Initiate PO Request
                                        </Button>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    {/* Edit/Change Option */}
                                    {forwardStep === 'none' && (isPSO || isBank || isIncharge) && (
                                        (selectedRequest.status === 'pending' && (isPSO || isBank || isIncharge)) ||
                                        (selectedRequest.status === 'rejected' && (isPSO || isBank))
                                    ) && (
                                        <Button variant="primary" onClick={handleStartEditing}>
                                            {isIncharge ? 'Change Request' : 'Edit Request'}
                                        </Button>
                                    )}

                                    {/* Resubmit Option for PSO if status is rejected */}
                                    {forwardStep === 'none' && (isPSO || isBank) && selectedRequest.status === 'rejected' && (
                                        <Button
                                            variant="primary"
                                            onClick={() => handleOpenResubmitModal(selectedRequest)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                            disabled={isActionLoading}
                                        >
                                            <Icon icon={FiRotateCcw} className="mr-1 h-4 w-4 inline" />
                                            Resubmit
                                        </Button>
                                    )}

                                    {/* Incharge Actions */}
                                    {forwardStep === 'none' && (isIncharge || isBank) && selectedRequest.status === 'pending' && (
                                        <>
                                            <Button
                                                variant="primary"
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                                onClick={() => setForwardStep('ask_change')}
                                                disabled={isActionLoading}
                                            >
                                                Forward to Regional
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                className="border-red-500 bg-red-600 text-white hover:bg-red-700"
                                                onClick={() => {
                                                    setRejectReason('');
                                                    setShowRejectModal(true);
                                                }}
                                                disabled={isActionLoading}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}

                                    {/* Regional Actions */}
                                    {forwardStep === 'none' && (isRegional || isBank) && selectedRequest.status === 'forwarded_to_regional' && (
                                        <>
                                            <Button
                                                variant="primary"
                                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'forwarded')}
                                                disabled={isActionLoading}
                                            >
                                                Forward to Head
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                className="border-red-500 bg-red-600 text-white hover:bg-red-700"
                                                onClick={() => setShowRejectModal(true)}
                                                disabled={isActionLoading}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}

                                    {/* Head Actions */}
                                    {forwardStep === 'none' && (isHead || isBank) && selectedRequest.status === 'forwarded' && (
                                        <>
                                            <Button
                                                variant="primary"
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'approved')}
                                                disabled={isActionLoading}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                className="border-red-500 bg-red-600 text-white hover:bg-red-700"
                                                onClick={() => setShowRejectModal(true)}
                                                disabled={isActionLoading}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}

                                    {forwardStep === 'none' && (isPSO || isBank) && selectedRequest.status === 'approved' && (
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                initEmailComposer(selectedRequest);
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                                        >
                                            <Icon icon={FiMail} className="h-4 w-4" />
                                            Send Email
                                        </Button>
                                    )}

                                    {forwardStep === 'none' && isBank && (
                                        <Button
                                            variant="secondary"
                                            className="border-red-500 bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                                            onClick={() => handleDeleteRequest(selectedRequest!)}
                                            disabled={isActionLoading}
                                        >
                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                            Delete Request
                                        </Button>
                                    )}

                                    {forwardStep === 'none' && (
                                        <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>Close</Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Reject Reason Modal */}
            {showRejectModal && selectedRequest && (
                <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Provide Reject Reason" size="md" closeOnBackdropClick={false}>
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Rejection Reason *</label>
                            <MarkdownEditor
                                value={rejectReason}
                                onChange={(val) => setRejectReason(val)}
                                placeholder="Why is this request being rejected?"
                                rows={3}
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
                            <Button
                                variant="primary"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => handleUpdateStatus(selectedRequest, 'rejected')}
                                disabled={!rejectReason.trim() || isActionLoading}
                            >
                                Confirm Reject
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {showEditAddItemModal && (
                <Modal
                    isOpen={showEditAddItemModal}
                    onClose={() => setShowEditAddItemModal(false)}
                    title="Add Item Specification"
                    size="md"
                    closeOnBackdropClick={false}
                    footer={
                        <div className="flex justify-end gap-2 px-6 py-4">
                            <Button variant="secondary" onClick={() => setShowEditAddItemModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleEditAddItem}>Add Item</Button>
                        </div>
                    }
                >
                    <div className="px-6 py-5 space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Select Item *</label>
                            <SearchableItemSelect
                                items={filteredInventoryItems}
                                value={selectedEditInventoryItemId}
                                onChange={(inv) => {
                                    setSelectedEditInventoryItemId(inv.id);
                                    setEditNewItem(prev => ({
                                        ...prev,
                                        itemId: inv.id,
                                        itemCode: inv.itemCode || '',
                                        itemName: inv.itemCode ? `${inv.itemCode} - ${inv.itemName}` : (inv.itemName || ''),
                                        specification: inv.itemDescription || '',
                                        amount: String(inv.rates || 0),
                                        totalAmount: (inv.rates || 0) * (prev.quantity || 1)
                                    }));
                                }}
                                placeholder="Choose Item..."
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Specification</label>
                            <TextField
                                type="text"
                                value={editNewItem.specification}
                                onChange={() => {}}
                                placeholder="-"
                                readOnly={true}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Quantity</label>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEditNewItem(prev => ({ ...prev, quantity: Math.max(1, (Number(prev.quantity) || 1) - 1) }))}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 hover:bg-gray-200 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 select-none focus:outline-none transition-all"
                                    >
                                        <Icon icon={FiMinus} className="h-4 w-4" />
                                    </button>
                                    <TextField
                                        type="number"
                                        value={editNewItem.quantity}
                                        onChange={(e: any) => {
                                            const val = e.target.value;
                                            if (val === '') {
                                                setEditNewItem(prev => ({ ...prev, quantity: '' as any }));
                                            } else {
                                                const parsed = parseInt(val);
                                                setEditNewItem(prev => ({ ...prev, quantity: isNaN(parsed) ? 1 : Math.max(1, parsed) }));
                                            }
                                        }}
                                        onBlur={(e: any) => {
                                            if (!e.target.value || parseInt(e.target.value) < 1) {
                                                setEditNewItem(prev => ({ ...prev, quantity: 1 }));
                                            }
                                        }}
                                        onFocus={(e: any) => e.target.select()}
                                        onClick={(e: any) => e.target.select()}
                                        min="1"
                                        className="text-center w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setEditNewItem(prev => ({ ...prev, quantity: (Number(prev.quantity) || 1) + 1 }))}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 hover:bg-gray-200 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 select-none focus:outline-none transition-all"
                                    >
                                        <Icon icon={FiPlus} className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Unit *</label>
                                <select
                                    value={editNewItem.unit || 'piece'}
                                    onChange={(e: any) => setEditNewItem(prev => ({ ...prev, unit: e.target.value }))}
                                    className="form-control h-9 py-0 w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-medium text-gray-950 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-700 px-3"
                                >
                                    <option value="piece">piece</option>
                                    <option value="meter">meter</option>
                                    <option value="feet">feet</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Amount (Per Unit) *</label>
                                <TextField
                                    type="text"
                                    value={editNewItem.amount ? Number(editNewItem.amount).toLocaleString() : ''}
                                    onChange={() => {}}
                                    placeholder="-"
                                    readOnly={true}
                                    prefix={
                                        <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                            Rs.
                                        </span>
                                    }
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                <TextField
                                    type="text"
                                    value={((Number(editNewItem.amount) || 0) * (Number(editNewItem.quantity) || 1)).toLocaleString()}
                                    readOnly
                                    placeholder="Rs. 0"
                                    prefix={
                                        <span className="whitespace-nowrap inline-flex items-center rounded bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800/30 px-2 py-0.5 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider select-none">
                                            Rs.
                                        </span>
                                    }
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Location / Floor</label>
                                <TextField
                                    type="text"
                                    value={editNewItem.location}
                                    onChange={(e: any) => setEditNewItem(prev => ({ ...prev, location: e.target.value }))}
                                    placeholder="e.g. Ground Floor, Cash Counter"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Coverage Detail / Area</label>
                                <TextField
                                    type="text"
                                    value={editNewItem.coverageDetail}
                                    onChange={(e: any) => setEditNewItem(prev => ({ ...prev, coverageDetail: e.target.value }))}
                                    placeholder="e.g. Main Entrance Gate Area"
                                />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* PO Detail Modal */}
            {showPoModal && selectedPo && (
                <Modal
                    isOpen={showPoModal}
                    onClose={() => {
                        setShowPoModal(false);
                        setSelectedPo(null);
                    }}
                    title={`PO Details - ${selectedPo.poNumber || selectedPo.requestId}`}
                    size="xl"
                    closeOnBackdropClick={false}
                >
                    <div className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">PO Number</label>
                                <TextField type="text" value={selectedPo.poNumber || '-'} readOnly />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Request</label>
                                <TextField type="text" value={selectedPo.dateOfRequest || '-'} readOnly />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Category</label>
                                <TextField type="text" value={selectedPo.requestCategory || 'New'} readOnly />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                <TextField type="text" value={selectedPo.branchCode || '-'} readOnly />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                <TextField type="text" value={selectedPo.branchName || '-'} readOnly />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                <div className="h-9 flex items-center">
                                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 capitalize">{selectedPo.status}</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor</label>
                                <TextField type="text" value={selectedPo.vendor || '-'} readOnly />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                {isPSO ? (
                                    <div className="flex gap-2">
                                        <select
                                            value={poPsoStatus}
                                            onChange={(e) => setPoPsoStatus(e.target.value as 'installed' | 'not installed')}
                                            disabled={isActionLoading}
                                            className="form-control h-9 w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-semibold text-gray-900 dark:text-gray-100 rounded-lg px-2 border"
                                        >
                                            <option value="not installed">Not Installed</option>
                                            <option value="installed">Installed</option>
                                        </select>
                                        <Button
                                            variant="primary"
                                            onClick={handleUpdatePoPsoStatus}
                                            disabled={isActionLoading}
                                            className="h-9 py-1 px-3"
                                        >
                                            Update
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="h-9 flex items-center">
                                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${selectedPo.psoStatus === 'installed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
                                            {selectedPo.psoStatus === 'installed' ? 'Installed' : 'Not Installed'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {(isPSO || selectedPo.psoStatus === 'installed') && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200 font-medium">Installation Date</label>
                                    {isPSO && poPsoStatus === 'installed' ? (
                                        <TextField
                                            type="date"
                                            value={poInstallationDate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPoInstallationDate(e.target.value)}
                                            required
                                        />
                                    ) : (
                                        <TextField
                                            type="text"
                                            value={selectedPo.installationDate ? new Date(selectedPo.installationDate).toLocaleDateString() : '-'}
                                            readOnly
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1 font-bold">PO Items</label>
                            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                            <th className="px-3 py-2.5 text-center">S. NO</th>
                                            <th className="px-3 py-2.5 text-left">Item Name</th>
                                            <th className="px-3 py-2.5 text-center">Qty</th>
                                            <th className="px-3 py-2.5 text-right">Unit Price</th>
                                            <th className="px-3 py-2.5 text-right">Price</th>
                                            <th className="px-3 py-2.5 text-center">Tax %</th>
                                            <th className="px-3 py-2.5 text-right">Tax Amount</th>
                                            <th className="px-3 py-2.5 text-right">Price with Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                        {selectedPo.items && selectedPo.items.map((item: any, index: number) => {
                                            const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                            const itemDescription = item.itemDescription || inventoryItem?.itemDescription || '';
                                            const unitPriceWithTax = inventoryItem?.rates || 0;
                                            const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;

                                            const quantity = item.quantity || 0;
                                            const taxRate = itemTaxPercentage / 100;
                                            const totalPriceWithTax = unitPriceWithTax * quantity;
                                            const basePrice = totalPriceWithTax / (1 + taxRate);
                                            const unitBasePrice = quantity > 0 ? basePrice / quantity : 0;
                                            const tax = totalPriceWithTax - basePrice;

                                            return (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                    <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100">{index + 1}</td>
                                                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                                        <div>
                                                            <p className="font-semibold">{item.itemCode || item.itemName}</p>
                                                            {itemDescription && (
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">{itemDescription}</p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100 font-bold">{quantity}</td>
                                                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                                                        {unitPriceWithTax > 0 ? `Rs. ${unitBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                                                        {unitPriceWithTax > 0 ? `Rs. ${basePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                    </td>
                                                    <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100">
                                                        {unitPriceWithTax > 0 ? `${itemTaxPercentage}%` : 'N/A'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                                                        {unitPriceWithTax > 0 ? `Rs. ${tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                                                        {unitPriceWithTax > 0 ? `Rs. ${totalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(() => {
                                            let grandTotalBasePrice = 0;
                                            let grandTotalTax = 0;
                                            let grandTotalPriceWithTax = 0;

                                            if (selectedPo.items && Array.isArray(selectedPo.items)) {
                                                selectedPo.items.forEach((item: any) => {
                                                    const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                                    const unitPriceWithTax = inventoryItem?.rates || 0;
                                                    const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;
                                                    const quantity = item.quantity || 0;

                                                    if (unitPriceWithTax > 0) {
                                                        const taxRate = itemTaxPercentage / 100;
                                                        const totalPriceWithTax = unitPriceWithTax * quantity;
                                                        const basePrice = totalPriceWithTax / (1 + taxRate);
                                                        const tax = totalPriceWithTax - basePrice;

                                                        grandTotalBasePrice += basePrice;
                                                        grandTotalTax += tax;
                                                        grandTotalPriceWithTax += totalPriceWithTax;
                                                    }
                                                });
                                            }

                                            return (
                                                <tr className="bg-gray-100 dark:bg-gray-700/60 font-semibold text-gray-900 dark:text-gray-100">
                                                    <td colSpan={3} className="px-3 py-2.5 text-right text-sm">Grand Total:</td>
                                                    <td className="px-3 py-2.5 text-right">-</td>
                                                    <td className="px-3 py-2.5 text-right">Rs. {grandTotalBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                    <td className="px-3 py-2.5 text-center">-</td>
                                                    <td className="px-3 py-2.5 text-right">Rs. {grandTotalTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                    <td className="px-3 py-2.5 text-right text-blue-600 dark:text-blue-400 font-bold">Rs. {grandTotalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                            <div className="w-full rounded-xl border border-gray-200 p-3 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 bg-gray-50/50 min-h-[3rem] overflow-y-auto max-h-[10rem]">
                                <Markdown content={selectedPo.remarks || '-'} />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                            <Button variant="secondary" onClick={() => { setShowPoModal(false); setSelectedPo(null); }}>Close</Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Email Modal */}
            {showEmailModal && selectedRequest && (
                <Modal
                    isOpen={showEmailModal}
                    onClose={() => {
                        setShowEmailModal(false);
                        setEmailRecipients([]);
                        setEmailInput('');
                    }}
                    title="Send Approved Request Email"
                    size="lg"
                    closeOnBackdropClick={false}
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Recipients <span className="text-red-500">*</span>
                            </label>
                            <div className="min-h-[60px] rounded-xl border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                                <div className="flex flex-wrap gap-2">
                                    {emailRecipients.map((email, index) => (
                                        <span
                                            key={`${email}-${index}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 break-all font-semibold"
                                        >
                                            {email}
                                            <button
                                                type="button"
                                                onClick={() => removeEmailRecipient(email)}
                                                className="ml-1 rounded-full text-blue-600 hover:bg-blue-200 dark:text-blue-400 dark:hover:bg-blue-900/50"
                                            >
                                                <Icon icon={FiX} className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyDown={handleEmailInputKeyDown}
                                        placeholder="Type email and press Enter"
                                        className="flex-1 border-0 bg-transparent px-2 py-1 text-sm outline-none focus:ring-0 dark:text-white"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Type email address and press Enter to add recipient
                            </p>
                        </div>

                        {/* Tab Headers */}
                        <div className="mb-4 flex border-b border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                className={`pb-2 px-4 text-sm font-medium border-b-2 transition-all ${emailActiveTab === 'edit'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-bold'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                onClick={() => setEmailActiveTab('edit')}
                            >
                                Edit Email Text
                            </button>
                            <button
                                type="button"
                                className={`pb-2 px-4 text-sm font-medium border-b-2 transition-all ${emailActiveTab === 'preview'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-bold'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                onClick={() => setEmailActiveTab('preview')}
                            >
                                Live Preview Email
                            </button>
                        </div>

                        {emailActiveTab === 'edit' ? (
                            <div className="mb-4 space-y-4 max-h-[380px] overflow-y-auto pr-1">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Subject</label>
                                    <TextField
                                        value={emailSubject}
                                        onChange={(e) => setEmailSubject(e.target.value)}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Introduction</label>
                                    <MarkdownEditor
                                        value={emailIntro}
                                        onChange={(val) => setEmailIntro(val)}
                                        placeholder="Enter email introduction..."
                                        rows={4}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Outro Instructions</label>
                                    <MarkdownEditor
                                        value={emailOutro}
                                        onChange={(val) => setEmailOutro(val)}
                                        placeholder="Enter email outro..."
                                        rows={4}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Signature</label>
                                    <MarkdownEditor
                                        value={emailSignature}
                                        onChange={(val) => setEmailSignature(val)}
                                        placeholder="Enter email signature..."
                                        rows={4}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="mb-4 rounded-xl border border-gray-300 p-4 dark:border-gray-750 bg-white text-black overflow-auto max-h-[380px]">
                                <div dangerouslySetInnerHTML={{ __html: formatPsoRequestEmail(selectedRequest, vendors.find(v => v.companyName === selectedRequest.vendor || v.id === selectedRequest.vendorId || v.companyId === selectedRequest.vendorId), bankDetails?.name || 'Bank', emailSubject, emailIntro, emailOutro, emailSignature) }} />
                            </div>
                        )}

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setEmailRecipients([]);
                                    setEmailInput('');
                                }}
                                disabled={isSendingEmail}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSendEmail}
                                disabled={emailRecipients.length === 0 || isSendingEmail}
                                className="flex items-center gap-2"
                            >
                                {isSendingEmail ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon={FiMail} className="h-4 w-4" />
                                        Send Email
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Resubmit Modal */}
            {showResubmitModal && (
                <Modal
                    isOpen={showResubmitModal}
                    onClose={handleCloseResubmitModal}
                    title="Resubmit Request"
                    size="md"
                    closeOnBackdropClick={false}
                >
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Request Type</label>
                            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl dark:bg-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setResubmitType('BAMS')}
                                    className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition ${
                                        resubmitType === 'BAMS'
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
                                    }`}
                                >
                                    BAMS
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setResubmitType('CCTV')}
                                    className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition ${
                                        resubmitType === 'CCTV'
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
                                    }`}
                                >
                                    CCTV
                                </button>
                            </div>
                            {resubmitType !== ((resubmitRequestObj?.requestType as any) || 'BAMS') && (
                                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                    ⚠ Type change will generate a new {resubmitType}-RQ- request ID.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmit Reason (Optional)</label>
                            <textarea
                                value={resubmitReasonDraft}
                                onChange={(e) => setResubmitReasonDraft(e.target.value)}
                                className="form-control w-full outline-none bg-white dark:bg-gray-800 dark:border-gray-600 text-sm font-medium text-gray-900 dark:text-gray-100 rounded-lg p-2 border"
                                placeholder="Enter the reason why you are resubmitting this request (optional)..."
                                rows={3}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
                            <Button variant="secondary" onClick={handleCloseResubmitModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmResubmit}
                                disabled={isActionLoading}
                            >
                                {isActionLoading ? 'Resubmitting...' : 'Confirm Resubmit'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {showToast && <Toast message={toastMessage} type={toastType} onClose={() => setShowToast(false)} />}
        </>
    );
}
